import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import Header from "@/components/Header";
import { StripeOnboardingBanner } from "@/components/StripeOnboardingBanner";
import { PushOffBanner } from "@/components/PushOffBanner";
import { SellerRequestQueue } from "@/components/SellerRequestQueue";
import { trackEvent } from "@/lib/analytics";
import { SellerBalanceTile } from "@/components/SellerBalanceTile";
import { ProfileCompletionBanner } from "@/components/ProfileCompletionBanner";
import { SellerQRCode } from "@/components/SellerQRCode";
import { HeatPicker } from "@/components/HeatPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest } from "@/lib/queryClient";
import { ListingPhotoManager } from "@/components/ListingPhotoManager";
import {
  CLOTHING_CATEGORY,
  GARMENT_TYPES,
  CLOTHING_SIZES,
  CLOTHING_CONDITIONS,
  CLOTHING_VIBES,
} from "@shared/clothing";
import { formatOrderStatus, formatScheduledFor } from "@/lib/orderStatus";
import OrderChat from "@/components/OrderChat";
import { Plus, Package, AlertTriangle, Settings2, Camera, Upload, X, MessageCircle, Sparkles, Sprout, Receipt, ImageIcon, ArrowRight, Loader2 } from "lucide-react";
import { QuickAddListing } from "@/components/QuickAddListing";
import { useOpenRequestId, consumeOpenRequest } from "@/lib/listingComposer";
import { WeightBatchGrid } from "@/components/WeightBatchGrid";
import { SellerAddressFixer } from "@/components/SellerAddressFixer";
import { PickupWindowsCard } from "@/components/PickupWindowsCard";
import { FoundingMakerCelebration } from "@/components/FoundingMakerCelebration";
import { FoundingMakerShareKit } from "@/components/FoundingMakerShareKit";
import type { Item, Order, User } from "@shared/schema";
import { normalizeListingClassification } from "@shared/listing-taxonomy";

type SellerItem = Item & { seller: User };

type SellerOrderSelection = {
  id: string;
  orderId: string;
  sellerId: string;
  sellerName: string;
  fulfillmentProfileId?: string | null;
  fulfillmentMethod: "seller_pickup" | "drop_zone_pickup" | "local_delivery";
  buyerLabel: string;
  dropZoneName?: string | null;
  dropZoneCity?: string | null;
  dropZoneState?: string | null;
  scheduledDate: string;
  timeWindowLabel: string;
  dayLabel?: string | null;
  status: string;
  createdAt?: string | Date | null;
};

type SellerOrder = Order & {
  sellerSelections: SellerOrderSelection[];
  buyerName?: string;
  deliveryScheduledFor?: string | null;
  deliveryStatus?: string | null;
  deliveryRequestId?: string | null;
  deliveryDriverPay?: string | null;
  deliverySelfClaimable?: boolean;
  sellerSubtotal?: string | number | null;
};

const PICKUP_ORDER_STATUS_OPTIONS = [
  { value: "paid", label: "Paid" },
  { value: "preparing", label: "Preparing" },
  { value: "ready_for_pickup", label: "Ready for pickup" },
  { value: "picked_up", label: "Picked up" },
] as const;

const DELIVERY_ORDER_STATUS_OPTIONS = [
  { value: "paid", label: "Paid" },
  { value: "preparing", label: "Preparing" },
  { value: "out_for_delivery", label: "Out for delivery" },
  { value: "delivered", label: "Delivered" },
] as const;

function getSellerOrderStatusOptions(order: SellerOrder) {
  const status = String(order.status || "").trim().toLowerCase();
  const fulfillmentMethods = Array.from(
    new Set((order.sellerSelections || []).map((selection) => selection.fulfillmentMethod))
  );
  const isDelivery = fulfillmentMethods.includes("local_delivery");
  // On community-delivery orders a separate driver handles pickup and
  // drop-off (GPS + proof-photo gated), so the seller must not get the
  // driver-only "out for delivery" / "delivered" actions — their role
  // ends at marking the order ready (the handshake banner). Seller-delivery
  // orders are driven by the seller, so those actions stay.
  const isCommunityDelivery = order.deliveryMethod === "community_delivery";

  if (status === "cancel_requested") {
    return [
      { value: "refunded", label: "Approve cancellation & refund" },
      { value: "paid", label: "Keep order active" },
    ];
  }

  if (status === "paid") {
    return [
      { value: "preparing", label: "Start preparing" },
      { value: "refunded", label: "Cancel & refund" },
    ];
  }

  if (status === "preparing") {
    if (isCommunityDelivery) {
      // The seller marks the order ready; the assigned driver then handles
      // pickup and drop-off from the delivery screen (GPS + proof photo).
      return [
        { value: "ready_for_pickup", label: "Mark ready for pickup" },
        { value: "refunded", label: "Cancel & refund" },
      ];
    }
    return isDelivery
      ? [
          { value: "out_for_delivery", label: "Mark out for delivery" },
          { value: "refunded", label: "Cancel & refund" },
        ]
      : [
          { value: "ready_for_pickup", label: "Mark ready for pickup" },
          { value: "refunded", label: "Cancel & refund" },
        ];
  }

  if (status === "ready_for_pickup") {
    if (isCommunityDelivery) {
      // Pickup/delivery is the driver's job from here; the seller only keeps
      // the refund path.
      return [{ value: "refunded", label: "Cancel & refund" }];
    }
    return [
      { value: "picked_up", label: "Mark picked up" },
      { value: "refunded", label: "Cancel & refund" },
    ];
  }

  if (status === "out_for_delivery") {
    if (isCommunityDelivery) {
      // The assigned driver marks delivered from the delivery screen with
      // GPS + a proof photo. The seller only retains the refund path.
      return [{ value: "refunded", label: "Cancel & refund" }];
    }
    return [
      { value: "delivered", label: "Mark delivered" },
      { value: "refunded", label: "Cancel & refund" },
    ];
  }

  // Post-fulfilment: buyer-requested refund or seller-initiated goodwill
  // refund. Used to handle the cancel_requested approval path or to
  // proactively refund an upset buyer outside any pending request.
  if (status === "picked_up" || status === "delivered") {
    return [{ value: "refunded", label: "Issue refund" }];
  }

  return [];
}
type DropZone = {
  id: string;
  name: string;
  city: string;
  state: string;
  zipCode: string;
  pickupWindowLabel?: string | null;
  isActive: number;
};

type FulfillmentProfile = {
  id: string;
  sellerId: string;
  name: string;
  description?: string | null;
  fulfillmentMethod: "seller_pickup" | "drop_zone_pickup" | "local_delivery";
  movedBy: "buyer" | "seller" | "coordinator" | "courier";
  dropZoneId?: string | null;
  feeType: string;
  feeFlat?: string | null;
  minOrderSubtotal?: string | null;
  pickupWindowLabel?: string | null;
  isActive: number;
};

type ItemFulfillmentProfileLink =
  | {
      itemId?: string;
      fulfillmentProfileId?: string;
      profileId?: string;
      id?: string;
    }
  | {
      fulfillmentProfile?: {
        id?: string;
      };
      profile?: {
        id?: string;
      };
    };

type FulfillmentProfileTimeSlot = {
  id: string;
  fulfillmentProfileId: string;
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  sortOrder: number;
  isActive: number;
};

const METHOD_LABELS: Record<FulfillmentProfile["fulfillmentMethod"], string> = {
  seller_pickup: "Pick up from me",
  drop_zone_pickup: "Meet buyers at a community hub",
  local_delivery: "Bring it to them",
};

const DELIVERY_MOVED_BY_LABELS: Record<"seller" | "courier", string> = {
  seller: "I do",
  courier: "A delivery partner does",
};

const MOVED_BY_HELP: Record<"seller" | "courier", string> = {
  seller: "You drive this order to the buyer yourself.",
  courier:
    "A community driver picks it up and drives it door-to-door to the buyer — a paid delivery run.",
};

const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

type DayOfWeek = (typeof DAYS_OF_WEEK)[number];

const DAY_SHORT_LABELS: Record<DayOfWeek, string> = {
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat",
  Sunday: "Sun",
};

const CATEGORY_OPTIONS = [
  { value: "produce", label: "Produce" },
  { value: "eggs", label: "Eggs & Dairy" },
  { value: "baked", label: "Baked Goods" },
  { value: "prepared", label: "Prepared Foods" },
  { value: "meat", label: "Meat & Poultry" },
  { value: "pantry", label: "Pantry" },
  { value: "flowers", label: "Flowers" },
  { value: "homemade", label: "Homemade / Crafted" },
  { value: "garden", label: "Garden" },
  { value: "other", label: "Other" },
] as const;

const MEAT_SHARE_SIZES = [
  { value: "whole", label: "Whole animal", unit: "per animal" },
  { value: "half", label: "Half", unit: "per half share" },
  { value: "quarter", label: "Quarter", unit: "per quarter share" },
  { value: "eighth", label: "Eighth", unit: "per eighth share" },
];
const meatShareUnit = (size: string) =>
  MEAT_SHARE_SIZES.find((s) => s.value === size)?.unit ?? "per share";

// The AI Quick Add flow tags categories with a richer vocabulary than this
// dropdown, so an AI-created listing has to be mapped back onto CATEGORY_OPTIONS
// before the form can show it. This used to be a local 30-key table with its own
// "produce" fallback, which had drifted: it was missing candle (singular), soap,
// craft, plant and every clothing key, so opening one of those listings just to
// change the price silently re-filed it as produce on save. The shared taxonomy
// is a strict superset (78 keys, no disagreements) and falls back to "other".
function toFormCategory(cat: string | null | undefined): string {
  return normalizeListingClassification(cat).category;
}

const SUBTYPE_OPTIONS: Record<string, { value: string; label: string }[]> = {
  produce: [
    { value: "vegetables", label: "Veggie" },
    { value: "fruit", label: "Fruit" },
    { value: "herbs", label: "Herbs" },
    { value: "greens", label: "Greens" },
    { value: "mushrooms", label: "Mushrooms" },
    { value: "roots", label: "Roots" },
    { value: "tomatoes", label: "Tomatoes" },
  ],
  prepared: [
    { value: "meal", label: "Meal" },
    { value: "pasta", label: "Pasta" },
    { value: "sauce", label: "Sauce" },
    { value: "hot-sauce", label: "Hot Sauce" },
    { value: "snack", label: "Snack" },
    { value: "dessert", label: "Dessert" },
  ],
  baked: [
    { value: "bread", label: "Bread" },
    { value: "cookies", label: "Cookies" },
    { value: "pastry", label: "Pastry" },
    { value: "cake", label: "Cake" },
  ],
  meat: [
    { value: "beef", label: "Beef" },
    { value: "pork", label: "Pork" },
    { value: "poultry", label: "Poultry" },
    { value: "lamb", label: "Lamb" },
    { value: "share", label: "Live animal / share" },
    { value: "other", label: "Other" },
  ],
  eggs: [
    { value: "eggs", label: "Eggs" },
    { value: "milk", label: "Milk" },
    { value: "butter", label: "Butter" },
    { value: "cheese", label: "Cheese" },
    { value: "yogurt", label: "Yogurt" },
  ],
  pantry: [
    { value: "jam", label: "Jam / Preserves" },
    { value: "honey", label: "Honey / Syrup" },
    { value: "pickle", label: "Pickles" },
    { value: "ferment", label: "Ferments" },
    { value: "sauce", label: "Sauce / Condiment" },
    { value: "granola", label: "Granola / Cereal" },
    { value: "flour", label: "Flour" },
    { value: "grain", label: "Grains / Oats" },
    { value: "coffee", label: "Coffee / Tea" },
    { value: "beverage", label: "Beverage" },
    { value: "spice", label: "Spice / Seasoning" },
    { value: "dry-good", label: "Dry Good" },
  ],
  flowers: [
    { value: "bouquet", label: "Bouquet" },
    { value: "stem-bunch", label: "Stem Bunch" },
    { value: "arrangement", label: "Arrangement" },
    { value: "wreath", label: "Wreath" },
  ],
  homemade: [
    { value: "candle", label: "Candle" },
    { value: "soap", label: "Soap / Body" },
    { value: "textile", label: "Textile / Fiber" },
    { value: "ceramic", label: "Ceramic" },
    { value: "wood", label: "Woodwork" },
  ],
  garden: [
    { value: "seedling", label: "Seedling" },
    { value: "starter", label: "Starter Tray" },
    { value: "plant", label: "Plant" },
    { value: "soil", label: "Soil / Compost" },
  ],
};

function getSubtypeOptions(category?: string | null) {
  return SUBTYPE_OPTIONS[category || ""] || [];
}

const PACK_OPTIONS: Record<string, { value: string; label: string }[]> = {
  produce: [
    { value: "each", label: "Each (single item)" },
    { value: "by-weight", label: "By the pound" },
    { value: "bunch", label: "Bunch" },
    { value: "pint", label: "Pint" },
    { value: "quart", label: "Quart" },
    { value: "basket", label: "Basket" },
    { value: "peck", label: "Peck" },
    { value: "half-bushel", label: "Half bushel" },
    { value: "bushel", label: "Bushel" },
    { value: "bag", label: "Bag" },
  ],
  prepared: [
    { value: "single-serving", label: "Single serving" },
    { value: "family-pan", label: "Family pan" },
    { value: "jar", label: "Jar / tub" },
    { value: "pack", label: "Pack" },
  ],
  baked: [
    { value: "each", label: "Each (single item)" },
    { value: "loaf", label: "Loaf" },
    { value: "half-loaf", label: "Half loaf" },
    { value: "6-pack", label: "6-pack" },
    { value: "dozen", label: "Dozen" },
  ],
  eggs: [
    { value: "half-dozen", label: "Half dozen" },
    { value: "dozen", label: "Dozen" },
    { value: "carton", label: "Carton" },
    { value: "bundle", label: "Bundle" },
  ],
  pantry: [
    { value: "small-jar", label: "Small jar" },
    { value: "large-jar", label: "Large jar" },
    { value: "bag", label: "Bag" },
    { value: "pack", label: "Pack" },
  ],
  flowers: [
    { value: "single-stem", label: "Single stem" },
    { value: "small-bouquet", label: "Small bouquet" },
    { value: "medium-bouquet", label: "Medium bouquet" },
    { value: "large-bouquet", label: "Large bouquet" },
    { value: "stem-bunch", label: "Stem bunch" },
  ],
  homemade: [
    { value: "single", label: "Single item" },
    { value: "set", label: "Set" },
    { value: "small", label: "Small" },
    { value: "large", label: "Large" },
  ],
  garden: [
    { value: "single-pot", label: "Single pot" },
    { value: "tray", label: "Tray" },
    { value: "bundle", label: "Bundle" },
    { value: "bag", label: "Bag" },
  ],
};

const TIMING_OPTIONS: Record<string, { value: string; label: string }[]> = {
  produce: [
    { value: "harvested-today", label: "Harvested today" },
    { value: "picked-this-morning", label: "Picked this morning" },
    { value: "picked-this-week", label: "Picked this week" },
  ],
  prepared: [
    { value: "made-today", label: "Made today" },
    { value: "made-this-morning", label: "Made this morning" },
    { value: "ready-for-pickup", label: "Ready for pickup" },
  ],
  baked: [
    { value: "baked-today", label: "Baked today" },
    { value: "baked-this-morning", label: "Baked this morning" },
    { value: "frozen-fresh", label: "Frozen fresh" },
  ],
  eggs: [
    { value: "collected-today", label: "Collected today" },
    { value: "collected-this-week", label: "Collected this week" },
    { value: "ready-now", label: "Ready now" },
  ],
  pantry: [
    { value: "small-batch", label: "Small batch" },
    { value: "freshly-made", label: "Freshly made" },
    { value: "ready-now", label: "Ready now" },
  ],
  flowers: [
    { value: "wholesale", label: "Wholesale" },
    { value: "market", label: "Market" },
    { value: "event", label: "Event" },
  ],
  homemade: [
    { value: "made-this-week", label: "Made this week" },
    { value: "ready-now", label: "Ready now" },
    { value: "made-to-order", label: "Made to order" },
  ],
  garden: [
    { value: "ready-now", label: "Ready now" },
    { value: "hardened-off", label: "Hardened off" },
    { value: "started-this-season", label: "Started this season" },
  ],
};

const QUICK_DETAIL_OPTIONS: Record<string, { value: string; label: string }[]> = {
  produce: [
    { value: "heirloom", label: "Heirloom" },
    { value: "sweet", label: "Sweet" },
    { value: "crisp", label: "Crisp" },
    { value: "tender", label: "Tender" },
    { value: "use-soon", label: "Use soon" },
  ],
  prepared: [
    { value: "comfort-food", label: "Comfort food" },
    { value: "ready-to-heat", label: "Ready to heat" },
    { value: "vegetarian", label: "Vegetarian" },
    { value: "gluten-free", label: "Gluten free" },
  ],
  baked: [
    { value: "sweet", label: "Sweet" },
    { value: "savory", label: "Savory" },
    { value: "gluten-free", label: "Gluten free" },
    { value: "freezer-friendly", label: "Freezer friendly" },
  ],
  eggs: [
    { value: "pasture-raised", label: "Pasture raised" },
    { value: "farm-fresh", label: "Farm fresh" },
    { value: "small-batch", label: "Small batch" },
  ],
  pantry: [
    { value: "small-batch", label: "Small batch" },
    { value: "giftable", label: "Giftable" },
    { value: "seasonal", label: "Seasonal" },
  ],
  flowers: [
    { value: "bright", label: "Bright colors" },
    { value: "soft", label: "Soft palette" },
    { value: "giftable", label: "Giftable" },
    { value: "vase-ready", label: "Vase ready" },
  ],
  homemade: [
    { value: "giftable", label: "Giftable" },
    { value: "small-batch", label: "Small batch" },
    { value: "handmade", label: "Handmade" },
  ],
  garden: [
    { value: "pollinator-friendly", label: "Pollinator friendly" },
    { value: "beginner-friendly", label: "Beginner friendly" },
    { value: "sun-loving", label: "Sun loving" },
  ],
};

function getPackOptions(category?: string | null) {
  return PACK_OPTIONS[category || ""] || [];
}

function getTimingOptions(category?: string | null) {
  return TIMING_OPTIONS[category || ""] || [];
}

function getQuickDetailOptions(category?: string | null) {
  return QUICK_DETAIL_OPTIONS[category || ""] || [];
}

const LISTING_STEP_LABELS = [
  "Basics",
  "Category & type",
  "Quick optional",
  "More optional",
  "Fulfillment",
];

function getVisibleStepLabels(listingStep: number) {
  if (listingStep <= 2) return LISTING_STEP_LABELS.slice(0, 3);
  if (listingStep === 3) return LISTING_STEP_LABELS.slice(1, 4);
  return LISTING_STEP_LABELS.slice(2, 5);
}

const ANY_DAY_VALUE = "__ANY_DAY__";

const normalizeSelectedDaysFromLabel = (value?: string | null): DayOfWeek[] => {
  if (!value) return [];

  const normalized = value.trim().toLowerCase();
  if (
    normalized === "any day" ||
    normalized === "any days" ||
    normalized === "open to any day" ||
    normalized === "any"
  ) {
    return [];
  }

  const matches = DAYS_OF_WEEK.filter((day) =>
    normalized.includes(day.toLowerCase())
  );

  return matches;
};

const buildPickupWindowLabel = (selectedDays: DayOfWeek[]) => {
  if (selectedDays.length === 0) {
    return "Any day";
  }

  if (selectedDays.length === DAYS_OF_WEEK.length) {
    return "Any day";
  }

  return selectedDays.join(", ");
};

const getProfileIdFromLink = (link: ItemFulfillmentProfileLink): string | undefined => {
  if ("fulfillmentProfileId" in link && link.fulfillmentProfileId) {
    return link.fulfillmentProfileId;
  }

  if ("profileId" in link && link.profileId) {
    return link.profileId;
  }

  if ("fulfillmentProfile" in link && link.fulfillmentProfile?.id) {
    return link.fulfillmentProfile.id;
  }

  if ("profile" in link && link.profile?.id) {
    return link.profile.id;
  }

  return undefined;
};

function AddHubInline({ onCreated }: { onCreated: (zone: DropZone) => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    addressLine1: "",
    city: "",
    state: "NY",
    zipCode: "",
    pickupWindowLabel: "",
    contactName: "",
  });

  const reset = () =>
    setForm({
      name: "",
      addressLine1: "",
      city: "",
      state: "NY",
      zipCode: "",
      pickupWindowLabel: "",
      contactName: "",
    });

  const createHubMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/drop-zones", form);
      return (await res.json()) as DropZone;
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["/api/drop-zones"] });
      onCreated(created);
      toast({ title: "Community hub added", description: created.name });
      reset();
      setOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Couldn't add hub",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const canSubmit =
    form.name.trim() && form.addressLine1.trim() && form.city.trim() && form.zipCode.trim();

  if (!open) {
    return (
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
        Add your own hub
      </Button>
    );
  }

  return (
    <div className="space-y-3 rounded-lg border border-dashed p-3">
      <p className="text-sm font-medium">Add a community hub</p>
      <div className="space-y-2">
        <Label htmlFor="hub-name">Hub name</Label>
        <Input
          id="hub-name"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="e.g., Eastside Community Center"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="hub-address">Street address</Label>
        <Input
          id="hub-address"
          value={form.addressLine1}
          onChange={(e) => setForm((f) => ({ ...f, addressLine1: e.target.value }))}
          placeholder="123 Main St"
        />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="col-span-2 space-y-2">
          <Label htmlFor="hub-city">City</Label>
          <Input
            id="hub-city"
            value={form.city}
            onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="hub-state">State</Label>
          <Input
            id="hub-state"
            value={form.state}
            onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="hub-zip">Zip</Label>
        <Input
          id="hub-zip"
          value={form.zipCode}
          onChange={(e) => setForm((f) => ({ ...f, zipCode: e.target.value }))}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="hub-window">Pickup window (optional)</Label>
        <Input
          id="hub-window"
          value={form.pickupWindowLabel}
          onChange={(e) => setForm((f) => ({ ...f, pickupWindowLabel: e.target.value }))}
          placeholder="e.g., Saturdays 9am-12pm"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="hub-contact">Contact name (optional)</Label>
        <Input
          id="hub-contact"
          value={form.contactName}
          onChange={(e) => setForm((f) => ({ ...f, contactName: e.target.value }))}
        />
      </div>
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          disabled={!canSubmit || createHubMutation.isPending}
          onClick={() => createHubMutation.mutate()}
        >
          {createHubMutation.isPending ? "Adding..." : "Save hub"}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => {
            reset();
            setOpen(false);
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

function readInitialDashboardTab(): "listings" | "fulfillment" | "orders" {
  if (typeof window === "undefined") return "listings";
  const tab = new URLSearchParams(window.location.search).get("tab");
  if (tab === "orders" || tab === "fulfillment") return tab;
  return "listings";
}

export default function SellerDashboard() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const initialDashboardTab = readInitialDashboardTab();
  const queryClient = useQueryClient();

  const [isListingDialogOpen, setIsListingDialogOpen] = useState(false);
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [chatOpenOrderId, setChatOpenOrderId] = useState<string | null>(null);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  // Which in-progress listing the dialog is showing. Null = a fresh one.
  const [quickAddDraftId, setQuickAddDraftId] = useState<string | null>(null);
  // Reopen the Instant Listing dialog on the draft tapped in the tray. Keyed on
  // the open-request id specifically, not the drafts array — requesting an open
  // doesn't change that array, so the effect never re-ran.
  const openRequestId = useOpenRequestId();
  useEffect(() => {
    if (!openRequestId) return;
    const requested = consumeOpenRequest();
    if (requested) {
      setQuickAddDraftId(requested);
      setShowQuickAdd(true);
    }
  }, [openRequestId]);
  const [showWeightBatch, setShowWeightBatch] = useState(false);
  const [weightBatchSeed, setWeightBatchSeed] = useState<{
    name: string;
    category: string;
    description: string;
    imageUrls: string[];
  } | null>(null);
  const [addressFixerOpen, setAddressFixerOpen] = useState(false);
  const [addressFixerError, setAddressFixerError] = useState<string | null>(null);
  const { data: unreadByOrder = {} } = useQuery<Record<string, number>>({
    queryKey: ["/api/my/unread-messages-by-order"],
    queryFn: async () => {
      const res = await fetch("/api/my/unread-messages-by-order", { credentials: "include" });
      if (!res.ok) return {};
      return res.json();
    },
    refetchInterval: 30_000,
  });
  const [editingItem, setEditingItem] = useState<SellerItem | null>(null);
  const [photoManagerItem, setPhotoManagerItem] = useState<SellerItem | null>(null);
  const [selectedProfileIds, setSelectedProfileIds] = useState<string[]>([]);
  const [isLoadingItemProfiles, setIsLoadingItemProfiles] = useState(false);
  const [returnToListingAfterOptionSave, setReturnToListingAfterOptionSave] = useState(false);
  const [isInlineOptionFormOpen, setIsInlineOptionFormOpen] = useState(false);
  const [listingCategory, setListingCategory] = useState("produce");
  const [listingSubtype, setListingSubtype] = useState("");
  const [listingHeatLevel, setListingHeatLevel] = useState<number | null>(null);
  const [listingQuickDetails, setListingQuickDetails] = useState<string[]>([]);
  const [listingGrowthState, setListingGrowthState] = useState<"ripe" | "growing">("ripe");
  const [listingDaysUntilRipe, setListingDaysUntilRipe] = useState("");
  const [listingIsMeatShare, setListingIsMeatShare] = useState(false);
  const [listingShareSize, setListingShareSize] = useState("quarter");
  const [listingProcessingFee, setListingProcessingFee] = useState("");
  const [listingSlaughterDate, setListingSlaughterDate] = useState("");
  const [listingBulkDiscountOn, setListingBulkDiscountOn] = useState(false);
  // Extra named price options for one listing ("Per dozen" $20 x10). Empty
  // means the listing keeps its single price and buyers see no picker.
  const [priceOptions, setPriceOptions] = useState<
    { label: string; price: string; qty: string }[]
  >([]);
  // Whether the listing being edited already had options. Without this, an
  // empty grid is ambiguous: "never had any" (skip the call) vs "seller just
  // deleted them all" (send an empty array to clear them).
  const [hadPriceOptions, setHadPriceOptions] = useState(false);
  const [listingBulkThreshold, setListingBulkThreshold] = useState("");
  const [listingBulkPercent, setListingBulkPercent] = useState("");
  const [listingStep, setListingStep] = useState(1);
  const [isFinalStepTransitioning, setIsFinalStepTransitioning] = useState(false);
  const [listingFilter, setListingFilter] = useState<"all" | "published" | "drafts">("all");
  const [optingOut, setOptingOut] = useState(false);
  const [orderFilter, setOrderFilter] = useState<"all" | "pending" | "completed">("all");
  const [showFoundingCelebration, setShowFoundingCelebration] = useState(false);
  const listingFormRef = useRef<HTMLFormElement | null>(null);
  const productNameInputRef = useRef<HTMLInputElement | null>(null);
  const priceInputRef = useRef<HTMLInputElement | null>(null);
  const qtyInputRef = useRef<HTMLInputElement | null>(null);

  const [imageUrlValue, setImageUrlValue] = useState("");
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [selectedImageName, setSelectedImageName] = useState("");
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isSelectingImage, setIsSelectingImage] = useState(false);
  const [imageInputResetKey, setImageInputResetKey] = useState(0);

  // Clothing listing mode (built for Safety Pin's 1-of-1 upcycled apparel).
  // Active when listingCategory === "clothing". Garment type is stored in
  // listingSubtype (the canonical column); the rest live in attributesJson.
  const [clothingImages, setClothingImages] = useState<string[]>([]);
  const [listingSize, setListingSize] = useState("");
  const [listingCondition, setListingCondition] = useState("reworked");
  const [listingColor, setListingColor] = useState("");
  const [listingChest, setListingChest] = useState("");
  const [listingLength, setListingLength] = useState("");
  const [listingWaist, setListingWaist] = useState("");
  const [listingInseam, setListingInseam] = useState("");
  const [listingVibes, setListingVibes] = useState<string[]>([]);
  const [listingFeatured, setListingFeatured] = useState(false);
  const [listingSalePrice, setListingSalePrice] = useState("");
  const [listingDescription, setListingDescription] = useState("");
  const [isFillingFromPhotos, setIsFillingFromPhotos] = useState(false);
  const [pendingClothingPhotos, setPendingClothingPhotos] = useState<string[] | null>(null);
  // True while an Instant Listing clothing hand-off is settling. The two-dialog
  // transition fires a phantom onOpenChange(false) on the listing dialog, whose
  // reset flips the category back to produce — ignore closes while this is set.
  const clothingHandoffRef = useRef(false);
  const isClothing = listingCategory === CLOTHING_CATEGORY;

  const [editingProfile, setEditingProfile] = useState<FulfillmentProfile | null>(null);
  const [profileName, setProfileName] = useState("");
  const [profileDescription, setProfileDescription] = useState("");
  const [fulfillmentMethod, setFulfillmentMethod] =
    useState<FulfillmentProfile["fulfillmentMethod"]>("seller_pickup");
  const [movedBy, setMovedBy] =
    useState<FulfillmentProfile["movedBy"]>("seller");
  const [dropZoneId, setDropZoneId] = useState<string>("");
  const [selectedDays, setSelectedDays] = useState<DayOfWeek[]>([]);
  const [specificDate, setSpecificDate] = useState<string>("");
  const [timeRestrictionMode, setTimeRestrictionMode] = useState<"any" | "specific">("any");
  const [timeSlots, setTimeSlots] = useState<
    { id?: string; dayOfWeek: DayOfWeek; startTime: string; endTime: string }[]
  >([]);
  const [profileTimeSlotsMap, setProfileTimeSlotsMap] = useState<
    Record<string, { dayOfWeek: string; startTime: string; endTime: string }[]>
  >({});

  const { data: items = [], refetch } = useQuery<SellerItem[]>({
    queryKey: ["/api/seller/items"],
    refetchOnMount: "always",
  });

  const { data: dropZones = [] } = useQuery<DropZone[]>({
    queryKey: ["/api/drop-zones"],
    queryFn: async () => {
      const res = await fetch("/api/drop-zones", { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  const { data: sellerOrders = [], refetch: refetchSellerOrders } = useQuery<SellerOrder[]>({
    queryKey: ["/api/seller/orders"],
    refetchOnMount: "always",
  });

  const isDemoAdmin = Boolean((user as any)?.isDemoAdmin);
  const resetDemoOrdersMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/demo/reset");
      return res.json() as Promise<{
        deletedOrderIds: string[];
        deletedNotificationCount: number;
      }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/seller/orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/my/orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/my/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/my/notifications/unread-count"] });
      toast({
        title: "Demo orders reset",
        description: `Deleted ${data.deletedOrderIds.length} order(s) and ${data.deletedNotificationCount} notification(s).`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Could not reset demo orders",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });
  const handleResetDemoOrders = () => {
    if (
      window.confirm(
        "Delete all demo orders and their notifications? Demo seller accounts and items will not be touched."
      )
    ) {
      resetDemoOrdersMutation.mutate();
    }
  };

  const { data: fulfillmentProfiles = [], refetch: refetchProfiles } = useQuery<FulfillmentProfile[]>({
    queryKey: ["/api/seller/fulfillment-profiles"],
    queryFn: async () => {
      const res = await fetch("/api/seller/fulfillment-profiles", { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  const activeDropZones = useMemo(
    () => dropZones.filter((zone) => Number(zone.isActive) === 1),
    [dropZones]
  );

  const handleSellerOrderStatusChange = async (orderId: string, status: string) => {
    trackEvent("order_action_tap", "/seller?tab=orders", {
      orderId,
      action: status,
      surface: "seller_dashboard",
    });
    try {
      const res = await apiRequest("POST", `/api/orders/${orderId}/status`, { status });
      const data = await res.json().catch(() => null);
      await refetchSellerOrders();
      const refund = data?.refund as
        | { attempted: boolean; issued: boolean; error: string | null; refundId: string | null }
        | undefined;
      if (refund?.attempted) {
        if (refund.issued) {
          toast({
            title: "Refund issued",
            description: refund.refundId
              ? `Order moved to ${formatOrderStatus(status)}. Stripe refund ${refund.refundId} sent to the buyer.`
              : `Order moved to ${formatOrderStatus(status)}. Refund sent to the buyer via Stripe.`,
          });
        } else {
          toast({
            title: "Order cancelled, refund failed",
            description: `Status moved to ${formatOrderStatus(status)} but the Stripe refund did not go through${refund.error ? `: ${refund.error}` : ""}. Issue the refund manually from your Stripe dashboard.`,
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Order updated",
          description: `Order moved to ${formatOrderStatus(status)}.`,
        });
      }
    } catch (error: any) {
      trackEvent("order_action_fail", "/seller?tab=orders", {
        orderId,
        action: status,
        surface: "seller_dashboard",
        message: String(error?.message || "").slice(0, 300),
      });
      toast({
        title: "Could not update order",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    }
  };

  const activeFulfillmentProfiles = useMemo(
    () => fulfillmentProfiles.filter((profile) => Number(profile.isActive) === 1),
    [fulfillmentProfiles]
  );

  // Include 'published_pending_payments' — these are live and visible to
  // buyers (just not yet purchasable, pending Stripe and/or compliance). They
  // must show in the seller's own list, or a seller who just listed sees an
  // empty tab and assumes it failed.
  const publishedItems = useMemo(
    () =>
      items.filter((item) => {
        const s = String((item as any).status || "published");
        return s === "published" || s === "published_pending_payments";
      }),
    [items]
  );

  const draftItems = useMemo(
    () => items.filter((item) => String((item as any).status || "published") === "draft"),
    [items]
  );

  const requestItems = useMemo(
    () => items.filter((item) => String((item as any).status || "") === "on_request"),
    [items]
  );

  const betaPhotoUploadEnabled = true;

  useEffect(() => {
    if (!isFinalStepTransitioning) return;

    const timer = window.setTimeout(() => {
      setIsFinalStepTransitioning(false);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [isFinalStepTransitioning]);

  useEffect(() => {
    let cancelled = false;

    async function loadProfileTimeSlots() {
      const entries = await Promise.all(
        fulfillmentProfiles.map(async (profile) => {
          try {
            const res = await fetch(`/api/seller/fulfillment-profiles/${profile.id}/time-slots`, {
              credentials: "include",
            });

            if (!res.ok) {
              return [profile.id, []] as const;
            }

            const slots = (await res.json()) as FulfillmentProfileTimeSlot[];
            return [
              profile.id,
              slots.map((slot) => ({
                dayOfWeek: slot.dayOfWeek,
                startTime: slot.startTime,
                endTime: slot.endTime,
              })),
            ] as const;
          } catch {
            return [profile.id, []] as const;
          }
        })
      );

      if (!cancelled) {
        setProfileTimeSlotsMap(Object.fromEntries(entries));
      }
    }

    if (fulfillmentProfiles.length > 0) {
      loadProfileTimeSlots();
    } else {
      setProfileTimeSlotsMap({});
    }

    return () => {
      cancelled = true;
    };
  }, [fulfillmentProfiles]);

  const totalItems = items.length;
  const totalInventory = items.reduce((sum, item) => sum + item.qtyAvailable, 0);
  const lowStockItems = items.filter((item) => item.qtyAvailable < 5).length;
  const growingItems = items.filter((item: any) => item.growthState === "growing");
  const growingNeedsPhoto = growingItems.filter((item: any) => {
    const last = item.lastGrowthPhotoAt ? new Date(item.lastGrowthPhotoAt).getTime() : 0;
    return Date.now() - last >= 7 * 86_400_000;
  }).length;

  const resetProfileForm = () => {
    setEditingProfile(null);
    setProfileName("");
    setProfileDescription("");
    setFulfillmentMethod("seller_pickup");
    setMovedBy("seller");
    setDropZoneId("");
    setSelectedDays([]);
    setSpecificDate("");
    setTimeRestrictionMode("any");
    setTimeSlots([]);
  };

  const resetListingImageState = () => {
    setImageUrlValue("");
    setImagePreviewUrl("");
    setSelectedImageName("");
    setIsUploadingImage(false);
    setIsSelectingImage(false);
    setImageInputResetKey((current) => current + 1);
  };

  useEffect(() => {
    if (!isProfileDialogOpen) {
      resetProfileForm();
    }
  }, [isProfileDialogOpen]);

  const resetClothingState = () => {
    setClothingImages([]);
    setListingSize("");
    setListingCondition("reworked");
    setListingColor("");
    setListingChest("");
    setListingLength("");
    setListingWaist("");
    setListingInseam("");
    setListingVibes([]);
    setListingFeatured(false);
    setListingSalePrice("");
    setListingDescription("");
  };

  useEffect(() => {
    if (!isListingDialogOpen) {
      resetListingImageState();
    setPriceOptions([]);
    setHadPriceOptions(false);
      resetClothingState();
      return;
    }

    const currentImageUrl = editingItem?.imageUrl || "";
    setImageUrlValue(currentImageUrl);
    setImagePreviewUrl(currentImageUrl);
    setSelectedImageName("");
    setImageInputResetKey((current) => current + 1);

    // Load clothing attributes when editing a clothing item; otherwise clear.
    if ((editingItem as any)?.category === CLOTHING_CATEGORY) {
      const attrs = ((editingItem as any)?.attributesJson || {}) as any;
      const imgs = Array.isArray((editingItem as any)?.imagesJson)
        ? ((editingItem as any).imagesJson as string[])
        : currentImageUrl
          ? [currentImageUrl]
          : [];
      setClothingImages(imgs);
      setListingSize(attrs.size || "");
      setListingCondition(attrs.condition || "reworked");
      setListingColor(attrs.color || "");
      const m = attrs.measurements || {};
      setListingChest(m.chest || "");
      setListingLength(m.length || "");
      setListingWaist(m.waist || "");
      setListingInseam(m.inseam || "");
      setListingVibes(Array.isArray(attrs.vibes) ? attrs.vibes : []);
      setListingFeatured(Number((editingItem as any)?.isFeatured) === 1);
      setListingSalePrice(
        (editingItem as any)?.salePrice ? String((editingItem as any).salePrice) : "",
      );
      setListingDescription((editingItem as any)?.shortDescription || "");
    } else {
      resetClothingState();
    }
  }, [editingItem, isListingDialogOpen]);

  // Runs after the reset effect above (definition order): when Instant Listing
  // handed off a detected clothing photo, switch the just-opened dialog into
  // clothing mode with that photo and auto-fill the details from it.
  useEffect(() => {
    if (!isListingDialogOpen || !pendingClothingPhotos) return;
    const urls = pendingClothingPhotos.slice(0, 8);
    setPendingClothingPhotos(null);
    setListingCategory(CLOTHING_CATEGORY);
    setListingSubtype("");
    setClothingImages(urls);
    // Plain timeout with NO cleanup: nulling pendingClothingPhotos above re-runs
    // this effect, and a cleanup would cancel the fill before it fires. Runs
    // after the reset effect (definition order) so the photos aren't cleared.
    window.setTimeout(() => {
      void fillClothingFromPhotos(urls);
    }, 250);
    // The dialog-to-dialog transition can leave a stuck `pointer-events: none`
    // on <body> (Radix), making the form inert. Clear it once open.
    window.setTimeout(() => {
      document.body.style.pointerEvents = "";
    }, 400);
    // Release the close-guard once the transition has settled so the user can
    // close the dialog normally afterward.
    window.setTimeout(() => {
      clothingHandoffRef.current = false;
    }, 1500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isListingDialogOpen, pendingClothingPhotos]);

  const createMutation = useMutation({
    mutationFn: async ({ data }: { data: any }) => {
      const res = await apiRequest("POST", "/api/items", data);
      const created = await res.json();
      if (created?.id) await savePriceOptions(created.id, data);
      return created;
    },
    onSuccess: (created: any) => {
      const becameFoundingMaker = created?.becameFoundingMaker === true;
      if (created?.fulfillmentAutoDefaulted) {
        toast({
          title: "Listed with pickup at your address",
          description:
            "We set pickup as the default so you're live fast. Add delivery or hub pickup anytime in Fulfillment.",
        });
        queryClient.invalidateQueries({ queryKey: ["/api/seller/fulfillment-profiles"] });
      } else {
        toast({ title: "Listing created" });
      }
      setIsListingDialogOpen(false);
      setEditingItem(null);
      setSelectedProfileIds([]);
      resetListingImageState();
    setPriceOptions([]);
    setHadPriceOptions(false);
      refetch();
      if (becameFoundingMaker) {
        queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
        setShowFoundingCelebration(true);
      }
    },
    onError: (error: any) => {
      if (error?.code === "seller_needs_address") {
        setAddressFixerError(error.message);
        setAddressFixerOpen(true);
        return;
      }
      toast({
        title: "Failed to create listing",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Persist the listing's named price options. Only fires when the seller has
  // actually entered rows, so an ordinary single-price listing never touches
  // the endpoint. Each row becomes `qty` individually reservable units, which
  // is what keeps overselling impossible.
  const savePriceOptions = async (itemId: string, base?: { price?: any; unit?: any; qtyAvailable?: any }) => {
    const extras = priceOptions
      .map((o) => ({
        label: o.label.trim(),
        priceCents: Math.round(parseFloat(o.price || "0") * 100),
        qty: parseInt(o.qty || "0", 10),
      }))
      .filter((o) => o.label && o.priceCents > 0 && o.qty > 0);
    // The listing's own price has to be one of the options or it stops being
    // buyable the moment any option exists.
    const baseRow = {
      label: String(base?.unit ?? "each").trim() || "each",
      priceCents: Math.round(parseFloat(String(base?.price ?? "0")) * 100),
      qty: parseInt(String(base?.qtyAvailable ?? "0"), 10) || 10,
    };
    const rows =
      extras.length > 0 && baseRow.priceCents > 0
        ? [baseRow, ...extras.filter((o) => o.label.toLowerCase() !== baseRow.label.toLowerCase())]
        : extras;
    // Nothing to do for a listing that never had options. If it did and the
    // seller cleared the grid, POST the empty array so they're removed.
    if (rows.length === 0 && !hadPriceOptions) return;
    try {
      await apiRequest("POST", `/api/items/${itemId}/price-options`, { options: rows });
    } catch (err: any) {
      toast({
        title: "Listing saved, price options didn't",
        description: err?.message ?? "Try editing the listing and saving again.",
        variant: "destructive",
      });
    }
  };

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res = await apiRequest("PUT", `/api/items/${id}`, data);
      const saved = await res.json();
      await savePriceOptions(id, data);
      return saved;
    },
    onSuccess: () => {
      toast({ title: "Listing updated" });
      setIsListingDialogOpen(false);
      setEditingItem(null);
      setSelectedProfileIds([]);
      resetListingImageState();
    setPriceOptions([]);
    setHadPriceOptions(false);
      refetch();
    },
    onError: (error: any) => {
      if (error?.code === "seller_needs_address") {
        setAddressFixerError(error.message);
        setAddressFixerOpen(true);
        return;
      }
      toast({
        title: "Failed to update listing",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (itemId: string) => {
      await apiRequest("DELETE", `/api/items/${itemId}`);
    },
    onSuccess: () => {
      toast({ title: "Listing archived" });
      refetch();
      queryClient.invalidateQueries({ queryKey: ["/api/items"] });
      queryClient.invalidateQueries({ queryKey: ["/api/users/items"] });
      queryClient.invalidateQueries({ queryKey: ["/api/boxes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/items/popular"] });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to archive listing",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const createProfileMutation = useMutation({
    mutationFn: async ({
      data,
      slots,
    }: {
      data: any;
      slots: { dayOfWeek: string; startTime: string; endTime: string }[];
    }) => {
      const res = await apiRequest("POST", "/api/seller/fulfillment-profiles", data);
      const created = await res.json();

      if (slots.length > 0) {
        await syncProfileTimeSlots(created.id, slots);
      }

      return created;
    },
    onSuccess: (created) => {
      toast({ title: "Option created" });
      setIsProfileDialogOpen(false);
      refetchProfiles();

      if (returnToListingAfterOptionSave) {
        setSelectedProfileIds((current) =>
          current.includes(created.id) ? current : [...current, created.id]
        );
        setIsInlineOptionFormOpen(false);
        setReturnToListingAfterOptionSave(false);
        resetProfileForm();
        return;
      }

      resetProfileForm();
    },
    onError: (error: any) => {
      toast({
        title: "Failed to create option",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateProfileMutation = useMutation({
    mutationFn: async ({
      id,
      data,
      slots,
    }: {
      id: string;
      data: any;
      slots?: { dayOfWeek: string; startTime: string; endTime: string }[];
    }) => {
      const res = await apiRequest("PUT", `/api/seller/fulfillment-profiles/${id}`, data);
      const updated = await res.json();

      if (slots) {
        await syncProfileTimeSlots(id, slots);
      }

      return updated;
    },
    onSuccess: () => {
      toast({ title: "Option updated" });
      setIsProfileDialogOpen(false);
      resetProfileForm();
      refetchProfiles();
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update option",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteProfileMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/seller/fulfillment-profiles/${id}`);
    },
    onSuccess: () => {
      toast({ title: "Option deleted" });
      setIsProfileDialogOpen(false);
      resetProfileForm();
      refetchProfiles();
    },
    onError: (error: any) => {
      toast({
        title: "Failed to delete option",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const applyProfileToAllMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest(
        "POST",
        `/api/seller/fulfillment-profiles/${id}/apply-to-all`
      );
      return (await res.json()) as { itemCount: number; addedCount: number };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["/api/seller/items"] });
      queryClient.invalidateQueries({ queryKey: ["/api/items"] });
      toast({
        title:
          result.addedCount === 0
            ? "Already on all your listings"
            : `Added to ${result.addedCount} listing${result.addedCount === 1 ? "" : "s"}`,
        description:
          result.itemCount === 0
            ? "You don't have any active listings yet."
            : `This option now applies across your ${result.itemCount} active listing${result.itemCount === 1 ? "" : "s"}.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Couldn't apply to all listings",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });


  const validateStep1 = () => {
    const form = listingFormRef.current;
    if (!form) return "Complete the basics first.";

    const formData = new FormData(form);
    const name = String(formData.get("name") || "").trim();
    const price = Number(formData.get("price") || 0);

    if (isClothing) {
      if (!name) return "Name this piece.";
      if (price <= 0) return "Enter a price greater than zero.";
      if (!listingSubtype) return "Choose a garment type.";
      if (!listingSize) return "Choose a size.";
      if (clothingImages.length === 0) return "Add at least one photo.";
      return null;
    }

    const qtyAvailable = Number(formData.get("qtyAvailable") || 0);
    const unit = String(formData.get("unit") || "").trim();

    if (!name) return "Enter a product name.";
    if (price <= 0) return "Enter a price greater than zero.";
    if (qtyAvailable < 0) return "Quantity can't be negative.";
    // Zero is how a seller marks an existing listing sold out — every buyer
    // surface already renders qtyAvailable <= 0 as "Sold out". Only require
    // stock when creating, where publishing something with none is a mistake.
    if (!editingItem && qtyAvailable <= 0) return "Enter a quantity greater than zero.";
    if (!unit) return "Add a unit — what buyers get for the price (e.g. per dozen).";

    return null;
  };

  const validateStep2 = () => {
    const category = String(listingCategory || "").trim();
    const subtype = String(listingSubtype || "").trim();
    const subtypeOptions = getSubtypeOptions(category);

    if (!category) return "Choose a category.";
    if (subtypeOptions.length > 0 && !subtype) {
      return category === "produce" ? "Choose a produce type." : "Choose a subtype.";
    }

    return null;
  };

  const handleNextListingStep = () => {
    const error =
      listingStep === 1 ? validateStep1() :
      listingStep === 2 ? validateStep2() :
      null;

    if (error) {
      toast({
        title: "Finish this step first",
        description: error,
        variant: "destructive",
      });
      return;
    }

    if (listingStep === 4 || (isClothing && listingStep === 1)) {
      setIsFinalStepTransitioning(true);
    }

    setListingStep((current) => {
      // Clothing's fields all live in step 1, so it skips the food-only middle
      // steps (category / quick / more) and jumps straight to fulfillment.
      if (isClothing) return current === 1 ? 5 : Math.min(5, current + 1);
      return Math.min(5, current + 1);
    });
  };

  const handleBackListingStep = () => {
    setListingStep((current) => {
      if (isClothing) return current === 5 ? 1 : Math.max(1, current - 1);
      return Math.max(1, current - 1);
    });
  };

  const goToListingPhotoStep = () => {
    setListingStep(1);
    window.setTimeout(() => {
      document.getElementById("seller-photo-upload")?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 0);
  };

  const toggleListingQuickDetail = (value: string) => {
    setListingQuickDetails((current) =>
      current.includes(value)
        ? current.filter((entry) => entry !== value)
        : [...current, value]
    );
  };

  useEffect(() => {
    const allowed = new Set(getQuickDetailOptions(listingCategory).map((option) => option.value));
    setListingQuickDetails((current) => current.filter((value) => allowed.has(value)));
  }, [listingCategory]);

  const sellerLocationReady = Boolean(String((user as any)?.addressLine1 || "").trim() && String(user?.zipCode || "").trim());
  // A seller with no fulfillment options yet is not blocked: the server
  // auto-defaults to "pickup at my address" on publish so they can get listed
  // fast. Sellers who already have options still choose among them as before.
  const willAutoDefaultFulfillment = activeFulfillmentProfiles.length === 0;
  const fulfillmentReady = activeFulfillmentProfiles.length > 0 || willAutoDefaultFulfillment;
  const photoReady = isClothing
    ? clothingImages.length > 0
    : Boolean(String(imageUrlValue || "").trim());
  const requiredListingDetailsReady = !validateStep1() && !validateStep2();
  const canSaveDraft = requiredListingDetailsReady;
  const publishReady = requiredListingDetailsReady && sellerLocationReady && fulfillmentReady && photoReady;

  const handleSubmitListing = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const step1Error = validateStep1();
    if (step1Error) {
      toast({
        title: "Finish the required listing details",
        description: step1Error,
        variant: "destructive",
      });
      return;
    }

    const step2Error = validateStep2();
    if (step2Error) {
      toast({
        title: "Finish the required listing details",
        description: step2Error,
        variant: "destructive",
      });
      return;
    }

    const sellerZipCode = String(user?.zipCode || "").trim();
    const sellerAddressLine1 = String((user as any)?.addressLine1 || "").trim();
    // Everything but the photo done means the maker still gets a map pin and a
    // storefront card via on_request. Falling back to "draft" here silently
    // removed the pin the moment they edited a backfilled request listing
    // without adding a picture. Mirrors the server ternary in POST /api/items.
    const readyExceptPhoto =
      requiredListingDetailsReady && sellerLocationReady && fulfillmentReady && !photoReady;
    const targetStatus = publishReady ? "published" : readyExceptPhoto ? "on_request" : "draft";

    if (targetStatus === "published" && (!sellerZipCode || !sellerAddressLine1)) {
      toast({
        title: "Add your seller address before publishing",
        description: "Your listing location comes from your seller profile. Add your address details there first.",
        variant: "destructive",
      });
      return;
    }


    const formData = new FormData(e.currentTarget);

    const measurements: Record<string, string> = {};
    if (listingChest.trim()) measurements.chest = listingChest.trim();
    if (listingLength.trim()) measurements.length = listingLength.trim();
    if (listingWaist.trim()) measurements.waist = listingWaist.trim();
    if (listingInseam.trim()) measurements.inseam = listingInseam.trim();

    const payload = isClothing
      ? {
          name: String(formData.get("name") || ""),
          category: CLOTHING_CATEGORY,
          // Garment type is the canonical subtype; qty is 1 (each piece is 1-of-1).
          subtype: listingSubtype,
          price: String(formData.get("price") || ""),
          qtyAvailable: 1,
          unit: "each",
          shortDescription: listingDescription.trim(),
          imageUrl: clothingImages[0] || "",
          imagesJson: clothingImages,
          isFeatured: listingFeatured ? 1 : 0,
          salePrice: listingSalePrice.trim() ? listingSalePrice.trim() : null,
          status: targetStatus,
          zipCode: String(user?.zipCode || ""),
          attributesJson: {
            size: listingSize,
            condition: listingCondition,
            color: listingColor.trim() || undefined,
            measurements: Object.keys(measurements).length ? measurements : undefined,
            vibes: listingVibes,
          },
        }
      : {
          name: String(formData.get("name") || ""),
          category: String(formData.get("category") || "produce"),
          subtype: String(formData.get("subtype") || ""),
          heatLevel: listingSubtype === "hot-sauce" ? listingHeatLevel : null,
          bulkDiscountThresholdCents:
            listingBulkDiscountOn && Number(listingBulkThreshold) > 0 && Number(listingBulkPercent) > 0
              ? Math.round(Number(listingBulkThreshold) * 100)
              : null,
          bulkDiscountPercent:
            listingBulkDiscountOn && Number(listingBulkThreshold) > 0 && Number(listingBulkPercent) > 0
              ? Math.round(Number(listingBulkPercent))
              : null,
          price: String(formData.get("price") || ""),
          groceryAvg: String(formData.get("groceryAvg") || "") || null,
          qtyAvailable: Number(formData.get("qtyAvailable") || 0),
          unit: listingIsMeatShare
            ? meatShareUnit(listingShareSize)
            : String(formData.get("unit") || "").trim() || undefined,
          // Only sent for an already made-to-order item; omitted otherwise so the
          // partial update leaves isMadeToOrder/leadTimeDays untouched.
          leadTimeDays:
            editingItem && (editingItem as any).isMadeToOrder === 1
              ? Number(formData.get("leadTimeDays")) || null
              : undefined,
          freshnessNote: String(formData.get("freshnessNote") || ""),
          shortDescription: String(formData.get("shortDescription") || ""),
          imageUrl: imageUrlValue.trim(),
          status: targetStatus,
          zipCode: String(user?.zipCode || ""),
          growthState: listingIsMeatShare ? "ripe" : listingGrowthState,
          estimatedRipeDate:
            !listingIsMeatShare && listingGrowthState === "growing"
              ? new Date(
                  Date.now() + Math.max(1, Number(listingDaysUntilRipe) || 14) * 86_400_000,
                ).toISOString()
              : null,
          isMeatShare: listingIsMeatShare ? 1 : 0,
          processingFeeCents: listingIsMeatShare
            ? Math.round((Number(listingProcessingFee) || 0) * 100)
            : null,
          slaughterDate:
            listingIsMeatShare && listingSlaughterDate
              ? new Date(`${listingSlaughterDate}T12:00:00`).toISOString()
              : null,
          attributesJson: {
            harvestTiming: String(formData.get("harvestTiming") || ""),
            quickDetails: listingQuickDetails,
          },
        };

    if (editingItem) {
      updateMutation.mutate({
        id: editingItem.id,
        data: payload,
      });
    } else {
      createMutation.mutate({
        data: payload,
      });
    }
  };

  const handleDelete = (itemId: string) => {
    if (confirm("Archive this listing? It will be hidden from buyers and removed from your active listings.")) {
      deleteMutation.mutate(itemId);
    }
  };

  const [chargingSharesId, setChargingSharesId] = useState<string | null>(null);
  const handleChargeShares = async (itemId: string) => {
    if (
      !confirm(
        "Charge the processing fee to everyone who reserved a share now? This runs the real charge on their saved card.",
      )
    ) {
      return;
    }
    setChargingSharesId(itemId);
    try {
      const res = await apiRequest("POST", `/api/seller/items/${itemId}/charge-shares`, {});
      const data = await res.json();
      queryClient.invalidateQueries({ queryKey: ["/api/seller/reservations"] });
      toast({
        title:
          data.charged > 0
            ? `Charging ${data.charged} share${data.charged === 1 ? "" : "s"}`
            : "No reserved shares yet",
        description:
          data.charged > 0
            ? "Buyers are being charged their processing fee now."
            : undefined,
      });
    } catch (err: any) {
      toast({
        title: "Couldn't charge shares",
        description: err?.message || "Try again.",
        variant: "destructive",
      });
    } finally {
      setChargingSharesId(null);
    }
  };

  const handleManualImageUrlChange = (value: string) => {
    setImageUrlValue(value);
    setImagePreviewUrl(value.trim());
    setSelectedImageName("");
  };

  const handleRemoveSelectedImage = () => {
    setImageUrlValue("");
    setImagePreviewUrl("");
    setSelectedImageName("");
    setImageInputResetKey((current) => current + 1);
  };

  const handleImageFileSelected = async (file: File | null) => {
    setIsSelectingImage(false);

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast({
        title: "Please choose an image",
        description: "Only image files can be uploaded.",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      toast({
        title: "Image is too large",
        description: "Please choose an image smaller than 20MB.",
        variant: "destructive",
      });
      return;
    }

    const localPreviewUrl = URL.createObjectURL(file);
    setImagePreviewUrl(localPreviewUrl);
    setSelectedImageName(file.name);
    setIsUploadingImage(true);

    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await apiRequest("POST", "/api/uploads/item-image", formData);
      const payload = (await response.json()) as { imageUrl: string };

      setImageUrlValue(payload.imageUrl);
      setImagePreviewUrl(payload.imageUrl);

      toast({
        title: "Photo uploaded",
        description: "Your listing photo is ready to save.",
      });
    } catch (error: any) {
      setImagePreviewUrl("");
      setSelectedImageName("");
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      URL.revokeObjectURL(localPreviewUrl);
      setIsUploadingImage(false);
      setImageInputResetKey((current) => current + 1);
    }
  };

  // Multi-photo upload for clothing. Uploads each picked file through the same
  // single-image endpoint and appends the URLs (cover = first, capped at 8).
  const handleClothingImagesSelected = async (files: FileList | null) => {
    setIsSelectingImage(false);
    if (!files || files.length === 0) return;
    const picked = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (picked.some((f) => f.size > 20 * 1024 * 1024)) {
      toast({
        title: "Some photos are too large",
        description: "Each photo must be smaller than 20MB.",
        variant: "destructive",
      });
    }
    const usable = picked.filter((f) => f.size <= 20 * 1024 * 1024);
    if (usable.length === 0) return;

    setIsUploadingImage(true);
    try {
      const uploaded: string[] = [];
      for (const file of usable) {
        const formData = new FormData();
        formData.append("image", file);
        const response = await apiRequest("POST", "/api/uploads/item-image", formData);
        const payload = (await response.json()) as { imageUrl: string };
        uploaded.push(payload.imageUrl);
      }
      setClothingImages((current) => [...current, ...uploaded].slice(0, 8));
      toast({ title: uploaded.length === 1 ? "Photo added" : `${uploaded.length} photos added` });
    } catch (error: any) {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
    } finally {
      setIsUploadingImage(false);
      setImageInputResetKey((current) => current + 1);
    }
  };

  const removeClothingImage = (index: number) => {
    setClothingImages((current) => current.filter((_, i) => i !== index));
  };

  const toggleListingVibe = (value: string) => {
    setListingVibes((current) =>
      current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value],
    );
  };

  // Read the seller's uploaded photos and pre-fill the clothing fields. Size and
  // measurements are left blank on purpose — those come off the tag / a ruler.
  const fillClothingFromPhotos = async (urls: string[] = clothingImages) => {
    if (urls.length === 0) {
      toast({
        title: "Add photos first",
        description: "Add at least one photo, then I'll fill in the details for you.",
        variant: "destructive",
      });
      return;
    }
    setIsFillingFromPhotos(true);
    try {
      const res = await apiRequest("POST", "/api/ai/analyze-clothing", { imageUrls: urls });
      const a = await res.json();
      if (productNameInputRef.current && a.name) productNameInputRef.current.value = a.name;
      if (priceInputRef.current && (a.suggestedPriceMin || a.suggestedPriceMax)) {
        const mid =
          a.suggestedPriceMin && a.suggestedPriceMax
            ? Math.round((Number(a.suggestedPriceMin) + Number(a.suggestedPriceMax)) / 2)
            : Number(a.suggestedPriceMax || a.suggestedPriceMin);
        priceInputRef.current.value = String(mid);
      }
      if (a.garmentType) setListingSubtype(a.garmentType);
      if (a.color) setListingColor(a.color);
      if (a.condition) setListingCondition(a.condition);
      if (a.description) setListingDescription(a.description);
      if (Array.isArray(a.styleTags)) setListingVibes(a.styleTags);
      toast({
        title: "Details filled in",
        description: "Review and tweak anything, then set the size.",
      });
    } catch (error: any) {
      toast({
        title: "Couldn't read the photos",
        description: error?.message || "Fill in the details yourself and publish.",
        variant: "destructive",
      });
    } finally {
      setIsFillingFromPhotos(false);
    }
  };

  // Instant Listing detected apparel and uploaded the first photo — route the
  // seller straight into the clothing form with that photo, then auto-fill.
  const handleClothingDetected = (urls: string[]) => {
    // Close the Instant Listing dialog first, then open the clothing form after
    // it has fully dismissed. Opening a second Radix dialog in the same tick
    // leaves the body pointer-events:none and the new dialog inert (the
    // "can't click the Clothing tab" bug).
    clothingHandoffRef.current = true;
    setShowQuickAdd(false);
    window.setTimeout(() => {
      setEditingItem(null);
      setListingStep(1);
      setPendingClothingPhotos(urls);
      setIsListingDialogOpen(true);
    }, 320);
  };

  const buildProfilePayload = () => ({
    name: profileName,
    description: profileDescription || null,
    fulfillmentMethod,
    movedBy,
    dropZoneId: fulfillmentMethod === "drop_zone_pickup" ? dropZoneId || null : null,
    feeType: "none",
    pickupWindowLabel: buildPickupWindowLabel(selectedDays),
    specificDate: specificDate || null,
    isActive: 1,
  });

  const normalizedTimeSlots =
    timeRestrictionMode === "specific"
      ? timeSlots.filter((slot) => slot.dayOfWeek && slot.startTime && slot.endTime)
      : [];

  const handleCreateOrUpdateProfile = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const payload = buildProfilePayload();

    if (editingProfile) {
      updateProfileMutation.mutate({
        id: editingProfile.id,
        data: payload,
        slots: normalizedTimeSlots,
      });
      return;
    }

    createProfileMutation.mutate({
      data: payload,
      slots: normalizedTimeSlots,
    });
  };

  const toggleProfileSelection = (profileId: string) => {
    setSelectedProfileIds((current) =>
      current.includes(profileId)
        ? current.filter((id) => id !== profileId)
        : [...current, profileId]
    );
  };

  const toggleSelectedDay = (day: DayOfWeek) => {
    setSelectedDays((current) =>
      current.includes(day)
        ? current.filter((entry) => entry !== day)
        : [...current, day]
    );
  };

  const addTimeSlotRow = () => {
    setTimeRestrictionMode("specific");
    setTimeSlots((current) => [
      ...current,
      {
        dayOfWeek: selectedDays[0] || "Monday",
        startTime: "09:00",
        endTime: "12:00",
      },
    ]);
  };

  const updateTimeSlotRow = (
    index: number,
    field: "dayOfWeek" | "startTime" | "endTime",
    value: string
  ) => {
    setTimeSlots((current) =>
      current.map((slot, idx) =>
        idx === index ? { ...slot, [field]: value } : slot
      )
    );
  };

  const removeTimeSlotRow = (index: number) => {
    setTimeSlots((current) => current.filter((_, idx) => idx !== index));
  };

  const formatTimeLabel = (value: string) => {
    if (!value) return "";
    const [hourRaw, minute] = value.split(":");
    const hour = Number(hourRaw);
    const suffix = hour >= 12 ? "PM" : "AM";
    const normalizedHour = hour % 12 === 0 ? 12 : hour % 12;
    return `${normalizedHour}:${minute} ${suffix}`;
  };

  const buildTimeSummary = (slots: { dayOfWeek: string; startTime: string; endTime: string }[]) => {
    if (!slots.length) return "Any time";
    return slots
      .map((slot) => `${slot.dayOfWeek}: ${formatTimeLabel(slot.startTime)} - ${formatTimeLabel(slot.endTime)}`)
      .join(" • ");
  };

  const syncProfileTimeSlots = async (
    profileId: string,
    slots: { dayOfWeek: string; startTime: string; endTime: string }[]
  ) => {
    const existingRes = await fetch(`/api/seller/fulfillment-profiles/${profileId}/time-slots`, {
      credentials: "include",
    });

    if (!existingRes.ok) {
      throw new Error(await existingRes.text());
    }

    const existingSlots = (await existingRes.json()) as FulfillmentProfileTimeSlot[];

    await Promise.all(
      existingSlots.map(async (slot) => {
        const res = await fetch(`/api/seller/fulfillment-profile-time-slots/${slot.id}`, {
          method: "DELETE",
          credentials: "include",
        });
        if (!res.ok) {
          throw new Error(await res.text());
        }
      })
    );

    await Promise.all(
      slots.map(async (slot, index) => {
        const res = await fetch(`/api/seller/fulfillment-profiles/${profileId}/time-slots`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            dayOfWeek: slot.dayOfWeek,
            startTime: slot.startTime,
            endTime: slot.endTime,
            sortOrder: index,
            isActive: 1,
          }),
        });

        if (!res.ok) {
          throw new Error(await res.text());
        }
      })
    );

    setProfileTimeSlotsMap((current) => ({
      ...current,
      [profileId]: slots,
    }));
  };

  const loadItemProfileSelections = async (itemId: string) => {
    setIsLoadingItemProfiles(true);

    try {
      const res = await fetch(`/api/items/${itemId}/fulfillment-profiles`, {
        credentials: "include",
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      const links = (await res.json()) as ItemFulfillmentProfileLink[];

      const ids = links
        .map((link) => getProfileIdFromLink(link))
        .filter((value): value is string => Boolean(value));

      setSelectedProfileIds(ids);
    } catch (error: any) {
      setSelectedProfileIds([]);
      toast({
        title: "Failed to load options",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoadingItemProfiles(false);
    }
  };

  const openNewListingDialog = () => {
    setEditingItem(null);
    setSelectedProfileIds([]);
    setIsLoadingItemProfiles(false);
    setListingCategory("produce");
    setListingSubtype("");
    setListingHeatLevel(null);
    setListingGrowthState("ripe");
    setListingDaysUntilRipe("");
    setListingIsMeatShare(false);
    setListingShareSize("quarter");
    setListingProcessingFee("");
    setListingSlaughterDate("");
    setListingBulkDiscountOn(false);
    setListingBulkThreshold("");
    setListingBulkPercent("");
    setListingStep(1);
    resetListingImageState();
    setPriceOptions([]);
    setHadPriceOptions(false);
    resetClothingState();
    setIsListingDialogOpen(true);
  };

  // Auto-open the AI Quick Add panel when arriving via the BottomNav "+"
  // FAB (which navigates to /seller?action=add). Strip the param once
  // consumed so a page refresh doesn't re-open it.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const action = params.get("action");
    if (action !== "add" && action !== "add-manual") return;
    if (action === "add-manual") {
      openNewListingDialog();
    } else {
      setShowQuickAdd(true);
    }
    params.delete("action");
    const qs = params.toString();
    const clean = window.location.pathname + (qs ? `?${qs}` : "");
    window.history.replaceState({}, "", clean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tapping the BottomNav "+" FAB while already on the dashboard can't rely on
  // the ?action=add mount effect above (it doesn't re-run), so the FAB fires
  // this event instead. See BottomNav and CLAUDE.md trap #13.
  useEffect(() => {
    const openQuickAdd = () => setShowQuickAdd(true);
    window.addEventListener("stoop:open-quick-add", openQuickAdd);
    return () => window.removeEventListener("stoop:open-quick-add", openQuickAdd);
  }, []);

  const openEditListingDialog = async (item: SellerItem) => {
    setEditingItem(item);
    // Load any existing named price options so editing shows them instead of
    // an empty grid. Units are one row per unit of stock, so collapse them back
    // to one row per label with a count. Weighed units carry no label and are
    // deliberately skipped — those belong to the bulk-weight flow.
    setPriceOptions([]);
    setHadPriceOptions(false);
    setHadPriceOptions(false);
    if ((item as any)?.soldByWeight) {
      (async () => {
        try {
          const res = await fetch(`/api/items/${item.id}`, { credentials: "include" });
          if (!res.ok) return;
          const detail = await res.json();
          const byLabel = new Map<string, { priceCents: number; qty: number }>();
          for (const u of (detail?.availableUnits ?? []) as any[]) {
            const label = String(u?.label ?? "").trim();
            if (!label) continue;
            const cur = byLabel.get(label);
            if (cur) cur.qty += 1;
            else byLabel.set(label, { priceCents: u.priceCents, qty: 1 });
          }
          setHadPriceOptions(byLabel.size > 0);
          // The listing's own price is stored as one of the options; it already
          // shows in the Price/Unit/Qty fields above, so don't repeat it as a
          // row. It is re-added on save from those fields.
          byLabel.delete(String((item as any)?.unit ?? "").trim());
          setPriceOptions(
            Array.from(byLabel.entries()).map(([label, v]) => ({
              label,
              price: (v.priceCents / 100).toFixed(2),
              qty: String(v.qty),
            })),
          );
        } catch {
          /* editing still works; the seller just re-enters options if needed */
        }
      })();
    }
    setListingCategory(toFormCategory((item as any)?.category));
    setListingSubtype((item as any)?.subtype || "");
    setListingHeatLevel((item as any)?.heatLevel ?? null);
    setListingQuickDetails(
      Array.isArray((item as any)?.attributesJson?.quickDetails)
        ? ((item as any).attributesJson.quickDetails as string[])
        : []
    );
    const itemGrowthState = (item as any)?.growthState === "growing" ? "growing" : "ripe";
    setListingGrowthState(itemGrowthState);
    if (itemGrowthState === "growing" && (item as any)?.estimatedRipeDate) {
      const remaining = Math.ceil(
        (new Date((item as any).estimatedRipeDate).getTime() - Date.now()) / 86_400_000,
      );
      setListingDaysUntilRipe(remaining > 0 ? String(remaining) : "");
    } else {
      setListingDaysUntilRipe("");
    }
    setListingIsMeatShare(Number((item as any)?.isMeatShare ?? 0) === 1);
    setListingShareSize(
      MEAT_SHARE_SIZES.find((s) => s.unit === String((item as any)?.unit ?? ""))?.value ?? "quarter",
    );
    setListingProcessingFee(
      (item as any)?.processingFeeCents != null
        ? (Number((item as any).processingFeeCents) / 100).toFixed(2)
        : "",
    );
    setListingSlaughterDate(
      (item as any)?.slaughterDate
        ? new Date((item as any).slaughterDate).toISOString().slice(0, 10)
        : "",
    );
    const bdThreshold = (item as any)?.bulkDiscountThresholdCents;
    const bdPercent = (item as any)?.bulkDiscountPercent;
    setListingBulkDiscountOn(bdThreshold != null && Number(bdPercent) > 0);
    setListingBulkThreshold(bdThreshold != null ? (Number(bdThreshold) / 100).toFixed(2) : "");
    setListingBulkPercent(bdPercent != null ? String(bdPercent) : "");
    setListingStep(1);
    setSelectedProfileIds([]);
    setIsListingDialogOpen(true);
    await loadItemProfileSelections(item.id);
  };

  const openCreateOptionFromListing = () => {
    setReturnToListingAfterOptionSave(true);
    resetProfileForm();
    setIsInlineOptionFormOpen(true);
  };

  const openEditProfileDialog = async (profile: FulfillmentProfile) => {
    setEditingProfile(profile);
    setProfileName(profile.name);
    setProfileDescription(profile.description || "");
    setFulfillmentMethod(profile.fulfillmentMethod);
    setMovedBy(profile.movedBy);
    setDropZoneId(profile.dropZoneId || "");
    setSelectedDays(normalizeSelectedDaysFromLabel(profile.pickupWindowLabel));
    setSpecificDate((profile as any).specificDate || "");

    try {
      const res = await fetch(`/api/seller/fulfillment-profiles/${profile.id}/time-slots`, {
        credentials: "include",
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      const slots = (await res.json()) as FulfillmentProfileTimeSlot[];
      setTimeSlots(
        slots.map((slot) => ({
          id: slot.id,
          dayOfWeek: slot.dayOfWeek as DayOfWeek,
          startTime: slot.startTime,
          endTime: slot.endTime,
        }))
      );
      setTimeRestrictionMode(slots.length > 0 ? "specific" : "any");
    } catch (error: any) {
      toast({
        title: "Failed to load time windows",
        description: error.message,
        variant: "destructive",
      });
      setTimeRestrictionMode("any");
      setTimeSlots([]);
    }

    setIsProfileDialogOpen(true);
  };

  const handleDeleteProfile = (profile: FulfillmentProfile) => {
    if (!confirm(`Delete "${profile.name}"? This cannot be undone.`)) {
      return;
    }

    deleteProfileMutation.mutate(profile.id);
  };

  const handleCreateInlineOption = () => {
    const payload = buildProfilePayload();

    createProfileMutation.mutate({
      data: payload,
      slots: normalizedTimeSlots,
    });
  };

  const handleCancelInlineOption = () => {
    setIsInlineOptionFormOpen(false);
    setReturnToListingAfterOptionSave(false);
    resetProfileForm();
  };

  const getProfileDayLabel = (profile: FulfillmentProfile) => {
    const normalizedDays = normalizeSelectedDaysFromLabel(profile.pickupWindowLabel);

    if (normalizedDays.length === 0) {
      return "Any day";
    }

    return normalizedDays.map((day) => DAY_SHORT_LABELS[day]).join(", ");
  };

  const handleProfileDialogOpenChange = (open: boolean) => {
    setIsProfileDialogOpen(open);

    if (!open) {
      setReturnToListingAfterOptionSave(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <FoundingMakerCelebration
        open={showFoundingCelebration}
        onClose={() => setShowFoundingCelebration(false)}
      />
      <Header />
      <PushOffBanner />
      <SellerRequestQueue />
      {requestItems.length > 0 && (
        <div className="mx-auto mt-3 w-full max-w-5xl px-4">
          <div className="rounded-xl border border-brand-secondary/30 bg-brand-secondary/5 p-4">
            <p className="text-sm font-semibold">
              {requestItems.length} listing{requestItems.length > 1 ? "s are" : " is"} open to
              requests
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              You're on the map so neighbors can find you and ask for{" "}
              {requestItems.length > 1 ? "these" : "this"}. They stay off the browse page until you
              add a photo and price, so nobody can order something you haven't finished listing.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={optingOut}
                onClick={async () => {
                  setOptingOut(true);
                  try {
                    await fetch("/api/seller/requests/withdraw", {
                      method: "POST",
                      credentials: "include",
                    });
                    await queryClient.invalidateQueries({ queryKey: ["/api/seller/items"] });
                    queryClient.invalidateQueries({ queryKey: ["/api/sellers"] });
                    queryClient.invalidateQueries({ queryKey: ["/api/users/items"] });
                    toast({ title: "Listings withdrawn", description: "They're private drafts again." });
                  } catch {
                    toast({ title: "Couldn't withdraw those", variant: "destructive" });
                  } finally {
                    setOptingOut(false);
                  }
                }}
                data-testid="button-withdraw-requests"
              >
                Remove these listings
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={optingOut}
                onClick={async () => {
                  setOptingOut(true);
                  try {
                    await fetch("/api/seller/map-visibility", {
                      method: "POST",
                      credentials: "include",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ onMap: false }),
                    });
                    queryClient.invalidateQueries({ queryKey: ["/api/sellers"] });
                    toast({
                      title: "Pin removed",
                      description: "You won't show on the map or in Growers & Makers.",
                    });
                  } catch {
                    toast({ title: "Couldn't remove your pin", variant: "destructive" });
                  } finally {
                    setOptingOut(false);
                  }
                }}
                data-testid="button-remove-pin"
              >
                Take me off the map
              </Button>
            </div>
          </div>
        </div>
      )}
      <StripeOnboardingBanner />
      <SellerBalanceTile />
      <ProfileCompletionBanner />

                <Dialog open={isProfileDialogOpen} onOpenChange={handleProfileDialogOpenChange}>
                  <DialogContent className="w-[95vw] max-w-xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
                    <DialogHeader>
                      <DialogTitle>
                        {editingProfile ? "Edit Option" : "Create Option"}
                      </DialogTitle>
                      <DialogDescription>
                        Choose a pickup or delivery option that fits your local setup.
                      </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleCreateOrUpdateProfile} className="space-y-4">
                      <div className="space-y-1.5">
                        <Label htmlFor="profile-name">Option Name</Label>
                        <Input
                          id="profile-name"
                          value={profileName}
                          onChange={(e) => setProfileName(e.target.value)}
                          placeholder="e.g., Saturday farmers market"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="profile-description">Description</Label>
                        <Textarea
                          id="profile-description"
                          value={profileDescription}
                          onChange={(e) => setProfileDescription(e.target.value)}
                          placeholder="Optional notes to help explain this option"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>How can people receive this item?</Label>
                        <Select
                          value={fulfillmentMethod}
                          onValueChange={(value: FulfillmentProfile["fulfillmentMethod"]) => {
                            setFulfillmentMethod(value);

                            if (value === "seller_pickup") {
                              setMovedBy("seller");
                              setDropZoneId("");
                            }

                            if (value === "drop_zone_pickup") {
                              setMovedBy("seller");
                            }

                            if (value === "local_delivery") {
                              setMovedBy("seller");
                              setDropZoneId("");
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choose an option" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="seller_pickup">Pick up from me</SelectItem>
                            <SelectItem value="drop_zone_pickup">Meet buyers at a community hub</SelectItem>
                            <SelectItem value="local_delivery">Bring it to them</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Which days work for this option?</Label>
                        <p className="text-sm text-muted-foreground">
                          Leave all days unchecked if you are open to any day.
                        </p>

                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            className={`rounded-md border px-3 py-2 text-sm ${
                              selectedDays.length === 0 ? "bg-muted" : ""
                            }`}
                            onClick={() => setSelectedDays([])}
                          >
                            Any day
                          </button>

                          {DAYS_OF_WEEK.map((day) => {
                            const selected = selectedDays.includes(day);

                            return (
                              <button
                                key={day}
                                type="button"
                                className={`rounded-md border px-3 py-2 text-sm ${
                                  selected ? "bg-muted" : ""
                                }`}
                                onClick={() => toggleSelectedDay(day)}
                              >
                                {day}
                              </button>
                            );
                          })}
                        </div>

                        <input type="hidden" value={selectedDays.length === 0 ? ANY_DAY_VALUE : selectedDays.join(",")} />

                        <div className="pt-2">
                          <Label htmlFor="option-specific-date">One-time date (optional)</Label>
                          <p className="text-sm text-muted-foreground">
                            For a one-off event like a bake sale or a village-wide garage sale.
                            Buyers can then only pick that exact date.
                          </p>
                          <Input
                            id="option-specific-date"
                            type="date"
                            className="mt-2"
                            value={specificDate}
                            onChange={(e) => setSpecificDate(e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="max-h-[42vh] space-y-3 overflow-y-auto rounded-lg border p-4 pr-2">
                        <div>
                          <Label>Time availability</Label>
                          <p className="text-sm text-muted-foreground">
                            Leave this open as any time, or add specific windows that work for you.
                          </p>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            className={`rounded-md border px-3 py-2 text-sm ${
                              timeRestrictionMode === "any" ? "bg-muted" : ""
                            }`}
                            onClick={() => {
                              setTimeRestrictionMode("any");
                              setTimeSlots([]);
                            }}
                          >
                            Any time
                          </button>

                          <button
                            type="button"
                            className={`rounded-md border px-3 py-2 text-sm ${
                              timeRestrictionMode === "specific" ? "bg-muted" : ""
                            }`}
                            onClick={() => {
                              setTimeRestrictionMode("specific");
                              if (timeSlots.length === 0) addTimeSlotRow();
                            }}
                          >
                            Specific time windows
                          </button>
                        </div>

                        {timeRestrictionMode === "specific" && (
                          <div className="space-y-2.5">
                            {timeSlots.map((slot, index) => (
                              <div
                                key={slot.id || `${slot.dayOfWeek}-${index}`}
                                className="grid gap-3 md:grid-cols-4 items-end"
                              >
                                <div className="space-y-1.5">
                                  <Label>Day</Label>
                                  <select
                                    value={slot.dayOfWeek}
                                    onChange={(e) => updateTimeSlotRow(index, "dayOfWeek", e.target.value)}
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                  >
                                    {DAYS_OF_WEEK.map((day) => (
                                      <option key={day} value={day}>
                                        {day}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                <div className="space-y-1.5">
                                  <Label>Start</Label>
                                  <Input
                                    type="time"
                                    value={slot.startTime}
                                    onChange={(e) => updateTimeSlotRow(index, "startTime", e.target.value)}
                                  />
                                </div>

                                <div className="space-y-2">
                                  <Label>End</Label>
                                  <Input
                                    type="time"
                                    value={slot.endTime}
                                    onChange={(e) => updateTimeSlotRow(index, "endTime", e.target.value)}
                                  />
                                </div>

                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => removeTimeSlotRow(index)}
                                >
                                  Remove
                                </Button>
                              </div>
                            ))}

                            <Button type="button" variant="outline" onClick={addTimeSlotRow}>
                              Add time window
                            </Button>
                          </div>
                        )}
                      </div>

                      {fulfillmentMethod === "drop_zone_pickup" && (
                        <>
                          <div className="space-y-2">
                            <Label>Which community hub?</Label>
                            <Select value={dropZoneId} onValueChange={setDropZoneId}>
                              <SelectTrigger>
                                <SelectValue placeholder="Choose a community hub" />
                              </SelectTrigger>
                              <SelectContent>
                                {activeDropZones.map((zone) => (
                                  <SelectItem key={zone.id} value={zone.id}>
                                    {zone.name} — {zone.city}, {zone.state}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {activeDropZones.length === 0 && (
                              <p className="text-xs leading-5 text-muted-foreground sm:text-sm">
                                No community hubs have been added yet.
                              </p>
                            )}
                            <AddHubInline onCreated={(zone) => setDropZoneId(zone.id)} />
                          </div>

                          <p className="text-xs leading-5 text-muted-foreground sm:text-sm">
                            You meet your buyers at the community hub to hand off their orders.
                          </p>
                        </>
                      )}

                      {fulfillmentMethod === "local_delivery" && (
                        <div className="space-y-2">
                          <Label>Who brings it to them?</Label>
                          <Select
                            value={movedBy}
                            onValueChange={(value: FulfillmentProfile["movedBy"]) => setMovedBy(value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Choose who brings it" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="seller">{DELIVERY_MOVED_BY_LABELS.seller}</SelectItem>
                              <SelectItem value="courier">{DELIVERY_MOVED_BY_LABELS.courier}</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">
                            {movedBy === "courier" ? MOVED_BY_HELP.courier : MOVED_BY_HELP.seller}
                          </p>
                        </div>
                      )}

                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          {editingProfile && (
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => handleDeleteProfile(editingProfile)}
                              disabled={deleteProfileMutation.isPending}
                            >
                              Delete
                            </Button>
                          )}
                        </div>

                        <div className="flex flex-col gap-2 sm:flex-row">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setIsProfileDialogOpen(false);
                              setReturnToListingAfterOptionSave(false);
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            disabled={
                              createProfileMutation.isPending ||
                              updateProfileMutation.isPending ||
                              (fulfillmentMethod === "drop_zone_pickup" && activeDropZones.length > 0 && !dropZoneId)
                            }
                          >
                            {editingProfile ? "Save Changes" : "Create Option"}
                          </Button>
                        </div>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>

      <main className="flex-1 container mx-auto px-4 py-6 sm:py-8">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold mb-2" data-testid="text-dashboard-title">
              Sell Dashboard
            </h1>
            <p className="text-muted-foreground">
              Manage your listings and pickup & delivery options
            </p>
          </div>
        </div>

        <Tabs defaultValue={initialDashboardTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 sm:inline-flex sm:w-auto">
            <TabsTrigger value="listings">Listings</TabsTrigger>
            <TabsTrigger value="fulfillment">Fulfillment</TabsTrigger>
            <TabsTrigger value="orders">Orders</TabsTrigger>
          </TabsList>

          <TabsContent value="listings" className="space-y-6">
            {Boolean(user?.isFoundingMaker) && <FoundingMakerShareKit />}

            {items.length === 0 && (
              <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                    <Sparkles className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">List a product to show up in the app</p>
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      Buyers can't find you until you have at least one listing. Snap a photo and
                      we'll fill in the details for you.
                    </p>
                    <div className="mt-3">
                      <Button
                        className="w-full sm:w-auto"
                        onClick={() => setShowQuickAdd(true)}
                      >
                        <Sparkles className="mr-2 h-4 w-4" />
                        Add a Listing
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Total Listings</CardDescription>
                  <CardTitle className="text-3xl">{totalItems}</CardTitle>
                </CardHeader>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Total Inventory</CardDescription>
                  <CardTitle className="text-3xl">{totalInventory}</CardTitle>
                </CardHeader>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Low Stock Items</CardDescription>
                  <CardTitle className="text-3xl flex items-center gap-2">
                    {lowStockItems}
                    {lowStockItems > 0 && <AlertTriangle className="h-5 w-5 text-amber-500" />}
                  </CardTitle>
                </CardHeader>
              </Card>
            </div>

            {growingItems.length > 0 && (
              <Link
                href="/seller/growth-photos"
                className="block rounded-lg border border-emerald-300/70 bg-emerald-50/60 p-4 transition hover:bg-emerald-50"
              >
                <div className="flex items-center gap-3">
                  <Sprout className="h-5 w-5 text-emerald-700" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-emerald-900">
                      {growingItems.length} item{growingItems.length === 1 ? "" : "s"} still growing
                    </div>
                    <div className="text-xs text-emerald-800/80">
                      {growingNeedsPhoto > 0
                        ? `${growingNeedsPhoto} need${growingNeedsPhoto === 1 ? "s" : ""} a fresh weekly photo — buyers can see the gap`
                        : "Weekly photos are up to date"}
                    </div>
                  </div>
                  <Camera className="h-4 w-4 text-emerald-700" />
                </div>
              </Link>
            )}

            <SellerQRCode />

            <Link
              href="/seller/expenses"
              className="block rounded-lg border border-border bg-card p-4 transition hover:bg-muted/50"
            >
              <div className="flex items-center gap-3">
                <Receipt className="h-5 w-5 text-primary" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">Track expenses & write-offs</div>
                  <div className="text-xs text-muted-foreground">
                    Log what you spend, attach receipts, export a spreadsheet at tax time
                  </div>
                </div>
                <Plus className="h-4 w-4 text-muted-foreground" />
              </div>
            </Link>

            <Card>
              <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle>Your Listings</CardTitle>
                  <CardDescription>Create and manage marketplace listings</CardDescription>
                </div>

                <div className="flex w-full sm:w-auto">
                  <Button
                    className="w-full sm:w-auto"
                    data-testid="button-instant-listing"
                    onClick={() => setShowQuickAdd(true)}
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Add a Listing
                  </Button>

                <Dialog
                  open={isListingDialogOpen}
                  onOpenChange={(open) => {
                    // Ignore the phantom close fired by the Instant Listing →
                    // clothing hand-off; it would reset the category to produce.
                    if (!open && clothingHandoffRef.current) {
                      return;
                    }
                    if (!open && (isUploadingImage || isSelectingImage)) {
                      return;
                    }

                    setIsListingDialogOpen(open);
                    if (!open) {
                      setEditingItem(null);
                      setSelectedProfileIds([]);
                      setIsLoadingItemProfiles(false);
                      setIsInlineOptionFormOpen(false);
                      setReturnToListingAfterOptionSave(false);
                      setListingCategory("produce");
                      setListingSubtype("");
                      setListingQuickDetails([]);
                      setListingGrowthState("ripe");
                      setListingDaysUntilRipe("");
                      setListingStep(1);
                      setIsFinalStepTransitioning(false);
                      setListingFilter("all");
                      resetProfileForm();
                      resetListingImageState();
    setPriceOptions([]);
    setHadPriceOptions(false);
                    }
                  }}
                >
                  <DialogContent
                    className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6"
                    onOpenAutoFocus={(event) => {
                      event.preventDefault();
                    }}
                  >
                    <DialogHeader>
                      <DialogTitle>
                        {editingItem ? "Edit Listing" : "Add New Listing"}
                      </DialogTitle>
                      <DialogDescription>
                        {editingItem
                          ? "Update your product listing details"
                          : "Create a new product listing for the marketplace"}
                      </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium">
                            {isClothing
                              ? `Step ${listingStep === 5 ? 2 : 1} of 2`
                              : `Step ${listingStep} of 5`}
                          </span>
                          <span className="text-muted-foreground">
                            {isClothing
                              ? listingStep === 5
                                ? "Publish"
                                : "Details"
                              : listingStep <= 2
                                ? "Required"
                                : listingStep <= 4
                                  ? "Optional"
                                  : "Publish"}
                          </span>
                        </div>

                        <div className="h-2 overflow-hidden rounded-full bg-muted">
                          <div
                            className="h-full rounded-full bg-primary transition-all"
                            style={{
                              width: `${(isClothing ? (listingStep === 5 ? 2 : 1) / 2 : listingStep / 5) * 100}%`,
                            }}
                          />
                        </div>

                        {!isClothing && (
                        <div className="text-xs">
                          <div className="flex gap-2 overflow-hidden sm:hidden">
                            {getVisibleStepLabels(listingStep).map((label) => {
                              const stepNumber = LISTING_STEP_LABELS.indexOf(label) + 1;
                              const active = listingStep === stepNumber;
                              const complete = listingStep > stepNumber;

                              return (
                                <span
                                  key={label}
                                  className={`min-w-0 flex-1 truncate rounded-full border px-2 py-1 text-center ${
                                    active
                                      ? "border-primary bg-primary/10 text-foreground"
                                      : complete
                                        ? "border-primary/30 bg-primary/5 text-muted-foreground"
                                        : "border-border bg-card text-muted-foreground"
                                  }`}
                                >
                                  {label}
                                </span>
                              );
                            })}
                          </div>

                          <div className="hidden gap-2 sm:flex sm:flex-wrap">
                            {LISTING_STEP_LABELS.map((label, index) => {
                              const stepNumber = index + 1;
                              const active = listingStep === stepNumber;
                              const complete = listingStep > stepNumber;

                              return (
                                <span
                                  key={label}
                                  className={`rounded-full border px-2.5 py-1 ${
                                    active
                                      ? "border-primary bg-primary/10 text-foreground"
                                      : complete
                                        ? "border-primary/30 bg-primary/5 text-muted-foreground"
                                        : "border-border bg-card text-muted-foreground"
                                  }`}
                                >
                                  {label}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                        )}
                      </div>

                    <form key={editingItem?.id ?? "new"} ref={listingFormRef} onSubmit={handleSubmitListing} className="space-y-4">
                      <div className={listingStep === 1 ? "space-y-3" : "hidden"}>
                        <div className="rounded-xl border bg-card/50 p-2.5 sm:p-3">
                          <p className="mb-2 font-medium">What are you listing?</p>
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                if (isClothing) {
                                  setListingCategory("produce");
                                  setListingSubtype("");
                                }
                              }}
                              className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                                !isClothing
                                  ? "border-primary bg-primary/10 text-primary"
                                  : "text-muted-foreground"
                              }`}
                            >
                              Food &amp; goods
                            </button>
                            <button
                              type="button"
                              onClick={() => setListingCategory(CLOTHING_CATEGORY)}
                              className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                                isClothing
                                  ? "border-primary bg-primary/10 text-primary"
                                  : "text-muted-foreground"
                              }`}
                            >
                              Clothing
                            </button>
                          </div>
                        </div>
                        <div className="rounded-xl border bg-card/50 p-2.5 sm:p-3">
                          <div className="mb-2">
                            <p className="font-medium">{isClothing ? "This piece" : "Basics"}</p>
                            <p className="text-xs leading-4 text-muted-foreground sm:text-sm">
                              {isClothing
                                ? "Each piece is one of one — buyers see it's unique."
                                : "Just the essentials to start a listing."}
                            </p>
                          </div>

                          <div className="space-y-3">
                            <div className="space-y-2">
                              <Label htmlFor="name">Product Name</Label>
                              <Input
                                ref={productNameInputRef}
                                id="name"
                                name="name"
                                defaultValue={editingItem?.name}
                                required
                                placeholder="e.g., Heirloom Tomatoes"
                                data-testid="input-item-name"
                                enterKeyHint="next"
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    priceInputRef.current?.focus();
                                  }
                                }}
                              />
                            </div>

                            <div className="grid gap-2.5 sm:grid-cols-2">
                              <div className="space-y-2">
                                <Label htmlFor="price">
                                  {listingIsMeatShare ? "Price / share ($)" : "Price ($)"}
                                </Label>
                                <Input
                                  ref={priceInputRef}
                                  id="price"
                                  name="price"
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  defaultValue={editingItem?.price}
                                  required
                                  data-testid="input-item-price"
                                  enterKeyHint="next"
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      qtyInputRef.current?.focus();
                                    }
                                  }}
                                />
                              </div>

                              {!isClothing && (
                              <div className="space-y-2">
                                <Label htmlFor="qtyAvailable">
                                  {listingIsMeatShare ? "Shares available" : "Quantity"}
                                </Label>
                                <Input
                                  ref={qtyInputRef}
                                  id="qtyAvailable"
                                  name="qtyAvailable"
                                  type="number"
                                  min="0"
                                  defaultValue={editingItem?.qtyAvailable ?? 10}
                                  required
                                  data-testid="input-item-qty"
                                  enterKeyHint="go"
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      handleNextListingStep();
                                    }
                                  }}
                                />
                              </div>
                              )}
                            </div>

                            {!isClothing && (
                            <div className="space-y-2">
                              <Label htmlFor="unit">Unit (what buyers get for the price)</Label>
                              <Input
                                id="unit"
                                name="unit"
                                required
                                defaultValue={(editingItem as any)?.unit ?? ""}
                                placeholder="e.g. per dozen, each, per lb"
                              />
                            </div>
                            )}

                            {!isClothing && (
                            <div className="space-y-2">
                              {/* Price / Unit / Qty, the same order and widths
                                  as QuickAddListing's option rows so the two
                                  listing forms don't disagree. */}
                              {priceOptions.length > 0 && (
                                <div className="grid grid-cols-3 gap-2">
                                  <Label className="text-xs">Price ($)</Label>
                                  <Label className="text-xs">Unit</Label>
                                  <Label className="text-xs">Qty available</Label>
                                </div>
                              )}
                              {priceOptions.map((opt, i) => (
                                <div key={i} className="grid grid-cols-3 items-end gap-2">
                                  <div className="min-w-0">
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      placeholder="20.00"
                                      value={opt.price}
                                      onChange={(e) =>
                                        setPriceOptions((cur) =>
                                          cur.map((o, j) => (j === i ? { ...o, price: e.target.value } : o)),
                                        )
                                      }
                                    />
                                  </div>
                                  <div className="min-w-0">
                                    <Input
                                      value={opt.label}
                                      placeholder="per dozen"
                                      onChange={(e) =>
                                        setPriceOptions((cur) =>
                                          cur.map((o, j) => (j === i ? { ...o, label: e.target.value } : o)),
                                        )
                                      }
                                    />
                                  </div>
                                  <div className="relative min-w-0">
                                    <Input
                                      type="number"
                                      min="1"
                                      max={60}
                                      className="min-w-0 pr-7"
                                      value={opt.qty}
                                      onChange={(e) =>
                                        setPriceOptions((cur) =>
                                          cur.map((o, j) => (j === i ? { ...o, qty: e.target.value } : o)),
                                        )
                                      }
                                    />
                                    <button
                                      type="button"
                                      aria-label="Remove option"
                                      className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                                      onClick={() => setPriceOptions((cur) => cur.filter((_, j) => j !== i))}
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-muted-foreground"
                                onClick={() =>
                                  setPriceOptions((cur) =>
                                    // Seed the first row from the listing's own
                                    // price — once options exist they are the
                                    // only way to buy, so it must be one of them.
                                    [...cur, { label: "", price: "", qty: "10" }],
                                  )
                                }
                                data-testid="button-add-price-option"
                              >
                                + Add another price option
                              </Button>
                              {priceOptions.length > 0 && (
                                <p className="text-xs text-muted-foreground">
                                  Buyers choose between your price above and these — use it
                                  for a half dozen and a dozen at different prices. Max 60 of each.
                                </p>
                              )}
                            </div>
                            )}

                            {!isClothing && (
                              <div className="space-y-2 rounded-lg border p-3">
                                <label className="flex items-start gap-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={listingBulkDiscountOn}
                                    onChange={(e) => setListingBulkDiscountOn(e.target.checked)}
                                    className="mt-0.5"
                                  />
                                  <span className="text-sm font-semibold text-foreground">
                                    Add a bulk discount
                                  </span>
                                </label>
                                {listingBulkDiscountOn && (
                                  <div className="grid grid-cols-2 gap-2">
                                    <div className="space-y-1">
                                      <Label className="text-xs">Spend at least ($)</Label>
                                      <Input
                                        type="number"
                                        inputMode="decimal"
                                        min={0}
                                        step="0.01"
                                        value={listingBulkThreshold}
                                        onChange={(e) => setListingBulkThreshold(e.target.value)}
                                        placeholder="e.g. 50"
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <Label className="text-xs">Discount (%)</Label>
                                      <Input
                                        type="number"
                                        inputMode="numeric"
                                        min={1}
                                        max={90}
                                        value={listingBulkPercent}
                                        onChange={(e) => setListingBulkPercent(e.target.value)}
                                        placeholder="e.g. 10"
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {isClothing && (
                              <div className="space-y-3">
                                <div className="grid gap-2.5 sm:grid-cols-2">
                                  <div className="space-y-2">
                                    <Label>Garment type</Label>
                                    <Select value={listingSubtype} onValueChange={setListingSubtype}>
                                      <SelectTrigger>
                                        <SelectValue placeholder="Pick a type" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {GARMENT_TYPES.map((g) => (
                                          <SelectItem key={g.value} value={g.value}>
                                            {g.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="space-y-2">
                                    <Label>Size</Label>
                                    <Select value={listingSize} onValueChange={setListingSize}>
                                      <SelectTrigger>
                                        <SelectValue placeholder="Pick a size" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {CLOTHING_SIZES.map((s) => (
                                          <SelectItem key={s} value={s}>
                                            {s}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>

                                <div className="grid gap-2.5 sm:grid-cols-2">
                                  <div className="space-y-2">
                                    <Label>Condition</Label>
                                    <Select value={listingCondition} onValueChange={setListingCondition}>
                                      <SelectTrigger>
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {CLOTHING_CONDITIONS.map((c) => (
                                          <SelectItem key={c.value} value={c.value}>
                                            {c.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="space-y-2">
                                    <Label htmlFor="cl-color">Color</Label>
                                    <Input
                                      id="cl-color"
                                      value={listingColor}
                                      onChange={(e) => setListingColor(e.target.value)}
                                      placeholder="e.g. black, washed denim"
                                    />
                                  </div>
                                </div>

                                <div className="space-y-2">
                                  <Label htmlFor="cl-desc">Description</Label>
                                  <Textarea
                                    id="cl-desc"
                                    value={listingDescription}
                                    onChange={(e) => setListingDescription(e.target.value)}
                                    placeholder="A sentence or two about the piece — the print, the vibe, the fit."
                                    className="min-h-[70px]"
                                  />
                                </div>

                                <div className="space-y-2">
                                  <Label>Measurements (optional)</Label>
                                  <p className="text-[11px] leading-4 text-muted-foreground">
                                    Thrifted sizing runs all over — measurements help buyers trust the fit. Inches.
                                  </p>
                                  <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                                    <Input value={listingChest} onChange={(e) => setListingChest(e.target.value)} placeholder="Pit to pit" inputMode="decimal" />
                                    <Input value={listingLength} onChange={(e) => setListingLength(e.target.value)} placeholder="Length" inputMode="decimal" />
                                    <Input value={listingWaist} onChange={(e) => setListingWaist(e.target.value)} placeholder="Waist" inputMode="decimal" />
                                    <Input value={listingInseam} onChange={(e) => setListingInseam(e.target.value)} placeholder="Inseam" inputMode="decimal" />
                                  </div>
                                </div>

                                <div className="space-y-2">
                                  <Label>Style tags (optional)</Label>
                                  <div className="flex flex-wrap gap-2">
                                    {CLOTHING_VIBES.map((v) => {
                                      const on = listingVibes.includes(v);
                                      return (
                                        <button
                                          key={v}
                                          type="button"
                                          onClick={() => toggleListingVibe(v)}
                                          className={`rounded-full border px-3 py-1 text-xs font-medium capitalize transition ${
                                            on
                                              ? "border-primary bg-primary/10 text-primary"
                                              : "text-muted-foreground"
                                          }`}
                                        >
                                          {v}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>

                                <div className="space-y-2 rounded-lg border bg-card/40 p-2.5">
                                  <Label>Shop sections (optional)</Label>
                                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                                    <input
                                      type="checkbox"
                                      checked={listingFeatured}
                                      onChange={(e) => setListingFeatured(e.target.checked)}
                                      className="h-4 w-4 accent-primary"
                                    />
                                    <span>Feature on my shop — pins it to the Featured shelf</span>
                                  </label>
                                  <div className="space-y-1">
                                    <Label htmlFor="cl-sale" className="text-xs font-normal text-muted-foreground">
                                      Sale price (optional) — adds it to the On Sale section
                                    </Label>
                                    <Input
                                      id="cl-sale"
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={listingSalePrice}
                                      onChange={(e) => setListingSalePrice(e.target.value)}
                                      placeholder="e.g. 28.00"
                                    />
                                  </div>
                                </div>
                              </div>
                            )}

                            {(editingItem as any)?.isMadeToOrder === 1 && (
                              <div className="space-y-2">
                                <Label htmlFor="leadTimeDays">Ready in (days)</Label>
                                <Input
                                  id="leadTimeDays"
                                  name="leadTimeDays"
                                  type="number"
                                  min={0}
                                  max={60}
                                  defaultValue={(editingItem as any)?.leadTimeDays ?? 3}
                                  placeholder="e.g. 3"
                                />
                                <p className="text-[11px] text-muted-foreground">
                                  Made-to-order item. Days after an order until it's ready. Buyers then see the earliest pickup on your next pickup day after that.
                                </p>
                              </div>
                            )}

                            {isClothing && (
                              <div className="space-y-2 rounded-lg border border-dashed p-2">
                                <div>
                                  <Label>Photos</Label>
                                  <p className="mt-0.5 text-[11px] leading-4 text-muted-foreground">
                                    Add a few — front, back, print detail, and on a body. First photo is the cover. No photo means draft only.
                                  </p>
                                </div>

                                {clothingImages.length > 0 && (
                                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                                    {clothingImages.map((url, i) => (
                                      <div
                                        key={url}
                                        className="relative aspect-square overflow-hidden rounded-md border bg-muted/20"
                                      >
                                        <img src={url} alt={`Photo ${i + 1}`} className="h-full w-full object-cover" />
                                        {i === 0 && (
                                          <span className="absolute left-1 top-1 rounded bg-primary/90 px-1.5 py-0.5 text-[9px] font-semibold text-primary-foreground">
                                            Cover
                                          </span>
                                        )}
                                        <button
                                          type="button"
                                          onClick={() => removeClothingImage(i)}
                                          className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white"
                                          aria-label="Remove photo"
                                        >
                                          <X className="h-3 w-3" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-primary bg-primary/5 px-3 py-1.5 text-sm font-medium text-primary">
                                    <Camera className="h-4 w-4" />
                                    <span>Take a photo</span>
                                    <input
                                      key={`cl-camera-${imageInputResetKey}`}
                                      type="file"
                                      accept="image/*"
                                      capture="environment"
                                      className="sr-only"
                                      onClick={() => setIsSelectingImage(true)}
                                      onChange={(e) => void handleClothingImagesSelected(e.target.files)}
                                    />
                                  </label>
                                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium">
                                    <ImageIcon className="h-4 w-4" />
                                    <span>Add from album</span>
                                    <input
                                      key={`cl-album-${imageInputResetKey}`}
                                      type="file"
                                      accept="image/*"
                                      multiple
                                      className="sr-only"
                                      onClick={() => setIsSelectingImage(true)}
                                      onChange={(e) => void handleClothingImagesSelected(e.target.files)}
                                    />
                                  </label>
                                </div>

                                {isUploadingImage && (
                                  <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                                    <Upload className="h-4 w-4 animate-pulse" />
                                    Uploading…
                                  </div>
                                )}

                                {clothingImages.length > 0 && (
                                  <>
                                    <Button
                                      type="button"
                                      className="w-full"
                                      disabled={isFillingFromPhotos || isUploadingImage}
                                      onClick={() => fillClothingFromPhotos()}
                                    >
                                      {isFillingFromPhotos ? (
                                        <>
                                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                          Reading your photos…
                                        </>
                                      ) : (
                                        <>
                                          <Sparkles className="mr-2 h-4 w-4" />
                                          Fill in the details from these photos
                                        </>
                                      )}
                                    </Button>
                                    <p className="text-[11px] leading-4 text-muted-foreground">
                                      We'll draft the name, type, color, description, price, and tags. You just add the size and publish.
                                    </p>
                                  </>
                                )}
                              </div>
                            )}

                            {!isClothing && betaPhotoUploadEnabled && (
                              <div className="space-y-1.5 rounded-lg border border-dashed p-2">
                                <div>
                                  <Label htmlFor="seller-photo-upload">Photo</Label>
                                  <p className="mt-0.5 text-[11px] leading-4 text-muted-foreground">
                                    No photo means draft only.
                                  </p>
                                </div>

                                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-primary bg-primary/5 px-3 py-1.5 text-sm font-medium text-primary">
                                    <Camera className="h-4 w-4" />
                                    <span>Take a photo</span>
                                    <input
                                      key={`camera-${imageInputResetKey}`}
                                      id="seller-photo-upload"
                                      type="file"
                                      accept="image/*"
                                      capture="environment"
                                      className="sr-only"
                                      onClick={() => {
                                        setIsSelectingImage(true);
                                      }}
                                      onChange={(e) => {
                                        const file = e.target.files?.[0] || null;
                                        void handleImageFileSelected(file);
                                      }}
                                    />
                                  </label>

                                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium">
                                    <ImageIcon className="h-4 w-4" />
                                    <span>Choose from album</span>
                                    <input
                                      key={`album-${imageInputResetKey}`}
                                      type="file"
                                      accept="image/*"
                                      className="sr-only"
                                      onClick={() => {
                                        setIsSelectingImage(true);
                                      }}
                                      onChange={(e) => {
                                        const file = e.target.files?.[0] || null;
                                        void handleImageFileSelected(file);
                                      }}
                                    />
                                  </label>

                                  {selectedImageName && (
                                    <span className="text-sm text-muted-foreground break-all">
                                      {selectedImageName}
                                    </span>
                                  )}

                                  {(imagePreviewUrl || imageUrlValue) && (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      onClick={handleRemoveSelectedImage}
                                    >
                                      <X className="mr-2 h-4 w-4" />
                                      Remove image
                                    </Button>
                                  )}
                                </div>

                                <p className="text-[11px] leading-4 text-muted-foreground">
                                  Please use your own photo of the actual item you're selling.
                                </p>

                                {isUploadingImage && (
                                  <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                                    <Upload className="h-4 w-4 animate-pulse" />
                                    Uploading photo…
                                  </div>
                                )}

                                {imagePreviewUrl && (
                                  <div className="overflow-hidden rounded-lg border bg-muted/20">
                                    <img
                                      src={imagePreviewUrl}
                                      alt="Listing preview"
                                      className="h-40 w-full object-cover"
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className={listingStep === 2 ? "space-y-4" : "hidden"}>
                        <div className="rounded-xl border bg-card/50 p-4">
                          <div className="mb-3">
                            <p className="font-medium">Category & type</p>
                            <p className="text-sm text-muted-foreground">
                              Choose the item category first, then the type, so the rest of the details can stay guided and lightweight.
                            </p>
                          </div>

                          <div className="grid gap-4 sm:grid-cols-2">
                            <div className="space-y-2">
                              <Label htmlFor="category">Category</Label>
                              <Select
                                name="category"
                                value={listingCategory}
                                onValueChange={(value) => {
                                  setListingCategory(value);
                                  setListingSubtype("");
                                  setListingHeatLevel(null);
                                }}
                              >
                                <SelectTrigger data-testid="select-category">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {CATEGORY_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="subtype">
                                {listingCategory === "produce" ? "Produce type" : "Subtype"}
                              </Label>

                              {getSubtypeOptions(listingCategory).length > 0 ? (
                                <Select
                                  name="subtype"
                                  value={listingSubtype}
                                  onValueChange={setListingSubtype}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Choose one" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {getSubtypeOptions(listingCategory).map((option) => (
                                      <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Input
                                  id="subtype"
                                  name="subtype"
                                  value={listingSubtype}
                                  onChange={(e) => setListingSubtype(e.target.value)}
                                  placeholder="Optional"
                                  enterKeyHint="go"
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      handleNextListingStep();
                                    }
                                  }}
                                />
                              )}
                            </div>
                          </div>

                          {listingSubtype === "hot-sauce" && (
                            <div className="mt-4 space-y-2">
                              <Label>Heat level</Label>
                              <p className="text-sm text-muted-foreground">
                                Rate the burn. Buyers see the flames and Scoville range on your listing.
                              </p>
                              <HeatPicker value={listingHeatLevel} onChange={setListingHeatLevel} />
                            </div>
                          )}
                        </div>
                      </div>

                      <div className={listingStep === 3 ? "space-y-4" : "hidden"}>
                        <div className="rounded-xl border bg-card/50 p-4">
                          <div className="mb-3 flex items-start justify-between gap-3">
                            <div>
                              <p className="font-medium">Quick optional details</p>
                              <p className="text-sm text-muted-foreground">
                                Add the most helpful extra details without making the listing feel long.
                              </p>
                            </div>
                            <span className="rounded-full border bg-card px-2.5 py-1 text-xs text-muted-foreground">
                              Optional
                            </span>
                          </div>

                      <div className="grid gap-4 sm:grid-cols-1">
                        <div className="space-y-2">
                          <Label htmlFor="groceryAvg">Grocery Avg ($)</Label>
                          <Input
                            id="groceryAvg"
                            name="groceryAvg"
                            type="number"
                            step="0.01"
                            min="0"
                            defaultValue={editingItem?.groceryAvg || ""}
                            placeholder="Optional"
                            data-testid="input-item-grocery"
                          />
                        </div>
                      </div>


                      <div className="space-y-2">
                        <Label htmlFor="harvestTiming">
                          {listingCategory === "produce"
                            ? "Harvest timing"
                            : listingCategory === "flowers"
                              ? "Harvest stage"
                              : "Made / ready timing"}
                        </Label>
                        {getTimingOptions(listingCategory).length > 0 ? (
                          <Select
                            name="harvestTiming"
                            defaultValue={(editingItem as any)?.attributesJson?.harvestTiming || ""}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Choose one" />
                            </SelectTrigger>
                            <SelectContent>
                              {getTimingOptions(listingCategory).map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            id="harvestTiming"
                            name="harvestTiming"
                            defaultValue={(editingItem as any)?.attributesJson?.harvestTiming || ""}
                            placeholder="Optional"
                          />
                        )}
                      </div>

                      {listingCategory === "produce" && (
                        <div className="rounded-lg border bg-emerald-50/60 p-3 space-y-2">
                          <div className="flex items-start gap-2">
                            <Sprout className="h-3.5 w-3.5 text-emerald-700 mt-0.5 shrink-0" />
                            <p className="text-sm font-semibold text-emerald-900">
                              Is this ready to sell, or still growing?
                            </p>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              type="button"
                              onClick={() => setListingGrowthState("ripe")}
                              className={`rounded-lg px-2 py-1.5 text-sm font-medium transition ${
                                listingGrowthState === "ripe"
                                  ? "bg-primary text-primary-foreground"
                                  : "border border-border bg-white"
                              }`}
                            >
                              Ready now
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setListingGrowthState("growing");
                                if (!listingDaysUntilRipe.trim()) setListingDaysUntilRipe("14");
                              }}
                              className={`rounded-lg px-2 py-1.5 text-sm font-medium transition ${
                                listingGrowthState === "growing"
                                  ? "bg-emerald-600 text-white"
                                  : "border border-border bg-white"
                              }`}
                            >
                              Still growing
                            </button>
                          </div>
                          {listingGrowthState === "growing" && (
                            <div className="space-y-1">
                              <Label className="text-xs text-emerald-900">
                                Days until ripe (estimate)
                              </Label>
                              <Input
                                type="number"
                                min={1}
                                max={180}
                                value={listingDaysUntilRipe}
                                onChange={(e) => setListingDaysUntilRipe(e.target.value)}
                                placeholder="e.g. 14"
                                className="h-9 bg-white"
                              />
                              <p className="text-xs text-emerald-800/80">
                                Buyers pay a 25% deposit to reserve. The remaining 75% auto-charges on the ripe date. Post a weekly progress photo so buyers know the crop is on track.
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {listingCategory === "meat" && (
                        <div className="rounded-lg border bg-rose-50/60 p-3 space-y-2">
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={listingIsMeatShare}
                              onChange={(e) => setListingIsMeatShare(e.target.checked)}
                              className="mt-0.5"
                            />
                            <span className="text-sm font-semibold text-rose-900">
                              This is a share of a live animal (meat share)
                            </span>
                          </label>
                          {listingIsMeatShare && (
                            <div className="space-y-2">
                              <p className="text-xs text-rose-800/80">
                                You're selling a live-animal share, not finished cuts. Pick the
                                share size, set the Price (step 1) to the price for ONE share, and
                                set Quantity to the number of shares the animal yields. Buyers pay
                                the share price in full now; the processing fee auto-charges on the
                                slaughter date.
                              </p>
                              <div className="space-y-1">
                                <Label className="text-xs text-rose-900">Share size</Label>
                                <div className="flex flex-wrap gap-1">
                                  {MEAT_SHARE_SIZES.map((s) => (
                                    <button
                                      key={s.value}
                                      type="button"
                                      onClick={() => setListingShareSize(s.value)}
                                      className={`rounded-full px-2.5 py-1 text-xs font-medium transition ${
                                        listingShareSize === s.value
                                          ? "bg-rose-600 text-white"
                                          : "border border-rose-300 bg-white text-rose-900"
                                      }`}
                                    >
                                      {s.label}
                                    </button>
                                  ))}
                                </div>
                                <p className="text-[11px] text-rose-800/70">
                                  Buyers see the price as "{meatShareUnit(listingShareSize)}."
                                </p>
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs text-rose-900">
                                  Processing fee per share ($)
                                </Label>
                                <Input
                                  type="number"
                                  min={0}
                                  step="0.01"
                                  value={listingProcessingFee}
                                  onChange={(e) => setListingProcessingFee(e.target.value)}
                                  placeholder="e.g. 95.00"
                                  className="h-9 bg-white"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs text-rose-900">Slaughter date</Label>
                                <Input
                                  type="date"
                                  value={listingSlaughterDate}
                                  onChange={(e) => setListingSlaughterDate(e.target.value)}
                                  className="h-9 bg-white"
                                />
                                <p className="text-xs text-rose-800/80">
                                  The processing fee auto-charges every reserved buyer on this date.
                                  You can also trigger it early from the listing.
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {getQuickDetailOptions(listingCategory).length > 0 && (
                        <div className="space-y-2">
                          <Label>Quick details</Label>
                          <div className="flex flex-wrap gap-2">
                            {getQuickDetailOptions(listingCategory).map((option) => {
                              const selected = listingQuickDetails.includes(option.value);

                              return (
                                <button
                                  key={option.value}
                                  type="button"
                                  onClick={() => toggleListingQuickDetail(option.value)}
                                  className={`rounded-full border px-3 py-1.5 text-sm transition ${
                                    selected
                                      ? "border-primary bg-primary/10 text-foreground"
                                      : "border-border bg-card text-muted-foreground"
                                  }`}
                                >
                                  {option.label}
                                </button>
                              );
                            })}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Optional quick tags to help buyers understand the product faster.
                          </p>
                        </div>
                      )}

                        </div>
                      </div>

                      <div className={listingStep === 4 ? "space-y-4" : "hidden"}>
                        <div className="rounded-xl border bg-card/50 p-4">
                          <div className="mb-3 flex items-start justify-between gap-3">
                            <div>
                              <p className="font-medium">More optional details</p>
                              <p className="text-sm text-muted-foreground">
                                Add a photo or a little more context if you want to make the listing stronger.
                              </p>
                            </div>
                            <span className="rounded-full border bg-card px-2.5 py-1 text-xs text-muted-foreground">
                              Optional
                            </span>
                          </div>

                      <div className="space-y-2">
                        <Label htmlFor="shortDescription">About this product</Label>
                        <Textarea
                          id="shortDescription"
                          name="shortDescription"
                          defaultValue={(editingItem as any)?.shortDescription || ""}
                          placeholder="One short note about what makes this item special"
                          rows={3}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="freshnessNote">Freshness Note</Label>
                        <Textarea
                          id="freshnessNote"
                          name="freshnessNote"
                          defaultValue={editingItem?.freshnessNote || ""}
                          placeholder="e.g., Picked this morning"
                          data-testid="textarea-item-freshness"
                        />
                      </div>

                        </div>
                      </div>

                      <div className={listingStep === 5 ? "space-y-4" : "hidden"}>
                        <div className="rounded-xl border bg-card/50 p-4">
                          <div className="mb-3">
                            <p className="font-medium">Fulfillment & publish</p>
                            <p className="text-sm text-muted-foreground">
                              Choose how buyers can receive the item. Once that is set, you can publish.
                            </p>
                          </div>

                          <div className="mb-4 rounded-lg border bg-muted/20 p-3">
                            <p className="text-sm font-medium">
                              {publishReady
                                ? "Ready to publish"
                                : !requiredListingDetailsReady
                                  ? "Finish the required listing details first"
                                  : !photoReady
                                    ? "This will save as a draft"
                                    : !sellerLocationReady
                                      ? "Add your seller address to publish"
                                      : !fulfillmentReady
                                        ? "Choose at least one pickup or delivery option"
                                        : "Almost ready to publish"}
                            </p>
                            <p className="mt-1 text-sm text-muted-foreground">
                              {publishReady
                                ? "Everything needed to publish is in place."
                                : !requiredListingDetailsReady
                                  ? "Go back and complete the required basics and category details."
                                  : !photoReady
                                    ? "Add a photo if you want this listing to publish instead of saving as a draft."
                                    : !sellerLocationReady
                                      ? "Your seller profile location is required before buyers can see this listing."
                                      : !fulfillmentReady
                                        ? "Pick at least one pickup or delivery option so buyers know how to receive it."
                                        : "Review the last few items below before publishing."}
                            </p>
                          </div>

                      <div className="space-y-3 rounded-lg border p-4">
                        <div>
                          <Label>Pickup & delivery options</Label>
                          <p className="text-sm text-muted-foreground mt-1">
                            Choose at least one option so people know how this item can be picked up or delivered.
                          </p>
                        </div>

                        {activeFulfillmentProfiles.length > 0 && (
                          <div className="space-y-2">
                            {activeFulfillmentProfiles.map((profile) => (
                              <div
                                key={profile.id}
                                className="flex items-center justify-between gap-2 rounded-md border bg-background/60 p-3"
                              >
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-medium">{profile.name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {METHOD_LABELS[profile.fulfillmentMethod]} · Days:{" "}
                                    <span className="font-medium text-foreground">{getProfileDayLabel(profile)}</span>
                                  </p>
                                </div>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="shrink-0"
                                  onClick={() => openEditProfileDialog(profile)}
                                >
                                  Edit days
                                </Button>
                              </div>
                            ))}
                            {!isInlineOptionFormOpen && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={openCreateOptionFromListing}
                              >
                                + Add a pickup or delivery option
                              </Button>
                            )}
                          </div>
                        )}

                        {isInlineOptionFormOpen && (
                          <div className="max-h-[44vh] space-y-4 overflow-y-auto rounded-lg border border-dashed p-4 pr-2">
                            <div>
                              <p className="font-medium">Create a new option</p>
                              <p className="text-sm text-muted-foreground">
                                Add it here, then it will show up in this listing automatically.
                              </p>
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="inline-profile-name">Option Name</Label>
                              <Input
                                id="inline-profile-name"
                                value={profileName}
                                onChange={(e) => setProfileName(e.target.value)}
                                placeholder="e.g., Saturday pickup from me"
                              />
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="inline-profile-description">Description</Label>
                              <Textarea
                                id="inline-profile-description"
                                value={profileDescription}
                                onChange={(e) => setProfileDescription(e.target.value)}
                                placeholder="Optional notes to help explain this option"
                              />
                            </div>

                            <div className="space-y-2">
                              <Label>How can people receive this item?</Label>
                              <Select
                                value={fulfillmentMethod}
                                onValueChange={(value: FulfillmentProfile["fulfillmentMethod"]) => {
                                  setFulfillmentMethod(value);

                                  if (value === "seller_pickup") {
                                    setMovedBy("seller");
                                    setDropZoneId("");
                                  }

                                  if (value === "drop_zone_pickup") {
                                    setMovedBy("seller");
                                  }

                                  if (value === "local_delivery") {
                                    setMovedBy("seller");
                                    setDropZoneId("");
                                  }
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Choose an option" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="seller_pickup">Pick up from me</SelectItem>
                                  <SelectItem value="drop_zone_pickup">Meet buyers at a community hub</SelectItem>
                                  <SelectItem value="local_delivery">Bring it to them</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="space-y-2">
                              <Label>Which days work for this option?</Label>
                              <p className="text-sm text-muted-foreground">
                                Leave all days unchecked if you are open to any day.
                              </p>

                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  className={`rounded-md border px-3 py-2 text-sm ${
                                    selectedDays.length === 0 ? "bg-muted" : ""
                                  }`}
                                  onClick={() => setSelectedDays([])}
                                >
                                  Any day
                                </button>

                                {DAYS_OF_WEEK.map((day) => {
                                  const selected = selectedDays.includes(day);

                                  return (
                                    <button
                                      key={day}
                                      type="button"
                                      className={`rounded-md border px-3 py-2 text-sm ${
                                        selected ? "bg-muted" : ""
                                      }`}
                                      onClick={() => toggleSelectedDay(day)}
                                    >
                                      {day}
                                    </button>
                                  );
                                })}
                              </div>

                              <div className="pt-2">
                                <Label htmlFor="option-specific-date-edit">
                                  One-time date (optional)
                                </Label>
                                <p className="text-sm text-muted-foreground">
                                  For a one-off event like a bake sale or a village-wide garage
                                  sale. Buyers can then only pick that exact date.
                                </p>
                                <Input
                                  id="option-specific-date-edit"
                                  type="date"
                                  className="mt-2"
                                  value={specificDate}
                                  onChange={(e) => setSpecificDate(e.target.value)}
                                />
                              </div>
                            </div>

                            <div className="space-y-3 rounded-lg border p-4">
                              <div>
                                <Label>Time availability</Label>
                                <p className="text-sm text-muted-foreground">
                                  Leave this open as any time, or add specific windows that work for you.
                                </p>
                              </div>

                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  className={`rounded-md border px-3 py-2 text-sm ${
                                    timeRestrictionMode === "any" ? "bg-muted" : ""
                                  }`}
                                  onClick={() => {
                                    setTimeRestrictionMode("any");
                                    setTimeSlots([]);
                                  }}
                                >
                                  Any time
                                </button>

                                <button
                                  type="button"
                                  className={`rounded-md border px-3 py-2 text-sm ${
                                    timeRestrictionMode === "specific" ? "bg-muted" : ""
                                  }`}
                                  onClick={() => {
                                    setTimeRestrictionMode("specific");
                                    if (timeSlots.length === 0) addTimeSlotRow();
                                  }}
                                >
                                  Specific time windows
                                </button>
                              </div>

                              {timeRestrictionMode === "specific" && (
                                <div className="space-y-3">
                                  {timeSlots.map((slot, index) => (
                                    <div
                                      key={slot.id || `${slot.dayOfWeek}-${index}`}
                                      className="grid gap-3 md:grid-cols-4 items-end"
                                    >
                                      <div className="space-y-2">
                                        <Label>Day</Label>
                                        <select
                                          value={slot.dayOfWeek}
                                          onChange={(e) => updateTimeSlotRow(index, "dayOfWeek", e.target.value)}
                                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                        >
                                          {DAYS_OF_WEEK.map((day) => (
                                            <option key={day} value={day}>
                                              {day}
                                            </option>
                                          ))}
                                        </select>
                                      </div>

                                      <div className="space-y-2">
                                        <Label>Start</Label>
                                        <Input
                                          type="time"
                                          value={slot.startTime}
                                          onChange={(e) => updateTimeSlotRow(index, "startTime", e.target.value)}
                                        />
                                      </div>

                                      <div className="space-y-2">
                                        <Label>End</Label>
                                        <Input
                                          type="time"
                                          value={slot.endTime}
                                          onChange={(e) => updateTimeSlotRow(index, "endTime", e.target.value)}
                                        />
                                      </div>

                                      <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => removeTimeSlotRow(index)}
                                      >
                                        Remove
                                      </Button>
                                    </div>
                                  ))}

                                  <Button type="button" variant="outline" onClick={addTimeSlotRow}>
                                    Add time window
                                  </Button>
                                </div>
                              )}
                            </div>

                            {fulfillmentMethod === "drop_zone_pickup" && (
                              <>
                                <div className="space-y-2">
                                  <Label>Which community hub?</Label>
                                  <Select value={dropZoneId} onValueChange={setDropZoneId}>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Choose a community hub" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {activeDropZones.map((zone) => (
                                        <SelectItem key={zone.id} value={zone.id}>
                                          {zone.name} — {zone.city}, {zone.state}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  {activeDropZones.length === 0 && (
                                    <p className="text-sm text-muted-foreground">
                                      No community hubs have been added yet.
                                    </p>
                                  )}
                                  <AddHubInline onCreated={(zone) => setDropZoneId(zone.id)} />
                                </div>

                                <p className="text-sm text-muted-foreground">
                                  You meet your buyers at the community hub to hand off their orders.
                                </p>
                              </>
                            )}

                            {fulfillmentMethod === "local_delivery" && (
                              <div className="space-y-2">
                                <Label>Who brings it to them?</Label>
                                <Select
                                  value={movedBy}
                                  onValueChange={(value: FulfillmentProfile["movedBy"]) => setMovedBy(value)}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Choose who brings it" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="seller">{DELIVERY_MOVED_BY_LABELS.seller}</SelectItem>
                                    <SelectItem value="courier">{DELIVERY_MOVED_BY_LABELS.courier}</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            )}

                            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                              <Button type="button" variant="outline" onClick={handleCancelInlineOption}>
                                Cancel option
                              </Button>
                              <Button
                                type="button"
                                onClick={handleCreateInlineOption}
                                disabled={
                                  !profileName.trim() ||
                                  createProfileMutation.isPending ||
                                  updateProfileMutation.isPending ||
                                  (fulfillmentMethod === "drop_zone_pickup" && activeDropZones.length > 0 && !dropZoneId)
                                }
                              >
                                Save option
                              </Button>
                            </div>
                          </div>
                        )}

                        {activeFulfillmentProfiles.length === 0 && (
                          <div className="space-y-3 rounded-lg border border-primary/30 bg-primary/5 p-4">
                            <div className="flex items-center gap-2">
                              <Package className="h-4 w-4 text-primary" />
                              <p className="text-sm font-semibold text-primary">
                                We'll set pickup at your address
                              </p>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              So you can get listed fast. Want to offer delivery or community hub pickup instead? Add those anytime.
                            </p>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={openCreateOptionFromListing}
                            >
                              Add a delivery or hub option
                            </Button>
                          </div>
                        )}

                        <div className="rounded-lg border bg-muted/10 p-4">
                          <div className="mb-3">
                            <p className="font-medium">Quick checklist</p>
                            <p className="text-sm text-muted-foreground">
                              Review location, photo, and fulfillment before you save or publish.
                            </p>
                          </div>

                          <div className="space-y-2">
                            <div className="flex min-w-0 flex-col items-start gap-3 rounded-lg border bg-background/70 p-3 sm:flex-row sm:items-center sm:justify-between">
                              <div>
                                <p className="text-sm font-medium">Location</p>
                                <p className={`mt-1 text-sm ${sellerLocationReady ? "text-muted-foreground" : "text-destructive"}`}>
                                  {sellerLocationReady
                                    ? "Using your seller profile address."
                                    : "Add your seller address in your profile before publishing."}
                                </p>
                              </div>

                              <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${sellerLocationReady ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                                {sellerLocationReady ? "Ready" : "Needs attention"}
                              </span>
                            </div>

                            <div className="flex min-w-0 flex-col items-start gap-3 rounded-lg border bg-background/70 p-3 sm:flex-row sm:items-center sm:justify-between">
                              <div className="min-w-0">
                                <p className="text-sm font-medium">Photo</p>
                                <p className="mt-1 text-sm text-muted-foreground">
                                  {photoReady
                                    ? "Ready to publish."
                                    : "No photo yet. This will save as a draft."}
                                </p>
                              </div>

                              <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-nowrap">
                                <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${photoReady ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                                  {photoReady ? "Ready" : "Draft only"}
                                </span>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="w-full sm:w-auto"
                                  onClick={goToListingPhotoStep}
                                >
                                  {photoReady ? "Change photo" : "Add photo"}
                                </Button>
                              </div>
                            </div>

                            <div className="flex min-w-0 flex-col items-start gap-3 rounded-lg border bg-background/70 p-3 sm:flex-row sm:items-center sm:justify-between">
                              <div>
                                <p className="text-sm font-medium">Fulfillment</p>
                                <p className={`mt-1 text-sm ${fulfillmentReady ? "text-muted-foreground" : "text-destructive"}`}>
                                  {willAutoDefaultFulfillment
                                    ? "Defaulting to pickup at your address."
                                    : fulfillmentReady
                                      ? "At least one option selected."
                                      : "Choose at least one option above."}
                                </p>
                              </div>

                              <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${fulfillmentReady ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                                {fulfillmentReady ? "Ready" : "Needs attention"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                        </div>
                      </div>

                      <div className="flex flex-col gap-3 border-t pt-4">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div className="text-sm text-muted-foreground">
                            {listingStep <= 2
                              ? "Required step"
                              : listingStep <= 4
                                ? "Optional step — can be skipped"
                                : publishReady
                                  ? "Ready to publish."
                                  : !photoReady
                                    ? "No photo yet — this will save as a draft."
                                    : "Finish the required items to publish."}
                          </div>

                          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                if (listingStep === 1) {
                                  setIsListingDialogOpen(false);
                                  setEditingItem(null);
                                  setSelectedProfileIds([]);
                                  setIsLoadingItemProfiles(false);
                                  resetListingImageState();
    setPriceOptions([]);
    setHadPriceOptions(false);
                                } else {
                                  handleBackListingStep();
                                }
                              }}
                            >
                              {listingStep === 1 ? "Cancel" : "Back"}
                            </Button>

                            {(listingStep === 3 || listingStep === 4) && (
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                  setIsFinalStepTransitioning(true);
                                  setListingStep(5);
                                }}
                              >
                                Skip for now
                              </Button>
                            )}

                            {listingStep < 5 ? (
                              <Button type="button" onClick={handleNextListingStep}>
                                Next
                              </Button>
                            ) : (
                              <Button
                                type="submit"
                                disabled={
                                  createMutation.isPending ||
                                  updateMutation.isPending ||
                                  isUploadingImage ||
                                  isLoadingItemProfiles ||
                                  isFinalStepTransitioning ||
                                  !canSaveDraft
                                }
                              >
                                {publishReady
                                  ? editingItem
                                    ? "Save Changes"
                                    : "Publish Listing"
                                  : "Save Draft"}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </form>
                    </div>
                  </DialogContent>
                </Dialog>
                </div>
              </CardHeader>

              <Dialog open={showQuickAdd} onOpenChange={setShowQuickAdd}>
                <DialogContent className="w-[95vw] max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6">
                  <DialogHeader className="sr-only">
                    <DialogTitle>Instant Listing</DialogTitle>
                    <DialogDescription>
                      Snap a photo of your item and we'll fill in the listing for you.
                    </DialogDescription>
                  </DialogHeader>
                  <QuickAddListing
                    key={quickAddDraftId ?? "new"}
                    openDraftId={quickAddDraftId}
                    onComplete={(created?: any) => {
                      setShowQuickAdd(false);
                      setQuickAddDraftId(null);
                      const becameFoundingMaker = created?.becameFoundingMaker === true;
                      if (becameFoundingMaker) {
                        queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
                        setShowFoundingCelebration(true);
                      }
                    }}
                    onCancel={() => {
                      setShowQuickAdd(false);
                      setQuickAddDraftId(null);
                    }}
                    onSwitchToWeightBatch={(seed) => {
                      setShowQuickAdd(false);
                      setWeightBatchSeed(seed);
                      setShowWeightBatch(true);
                    }}
                  />
                </DialogContent>
              </Dialog>

              <Dialog open={showWeightBatch} onOpenChange={setShowWeightBatch}>
                <DialogContent className="w-[95vw] max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6">
                  <DialogHeader className="sr-only">
                    <DialogTitle>List by weight (batch)</DialogTitle>
                    <DialogDescription>
                      List many individually-weighed units at once, grouped into sizes for buyers.
                    </DialogDescription>
                  </DialogHeader>
                  <WeightBatchGrid
                    onComplete={() => setShowWeightBatch(false)}
                    onCancel={() => setShowWeightBatch(false)}
                    initial={weightBatchSeed ?? undefined}
                  />
                </DialogContent>
              </Dialog>

              <Dialog open={addressFixerOpen} onOpenChange={setAddressFixerOpen}>
                <DialogContent className="w-[95vw] max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6">
                  <DialogHeader className="sr-only">
                    <DialogTitle>Update your pickup address</DialogTitle>
                    <DialogDescription>
                      Add your pickup address so buyers can see how close you are.
                    </DialogDescription>
                  </DialogHeader>
                  <SellerAddressFixer
                    initialError={addressFixerError}
                    title="Update your pickup address"
                    description="We need this before this listing can publish. Buyers see how close you are and community delivery uses it to route a driver."
                    onSaved={() => {
                      setAddressFixerError(null);
                      setAddressFixerOpen(false);
                      toast({
                        title: "Address saved",
                        description: "Try saving your listing again.",
                      });
                    }}
                    onCancel={() => {
                      setAddressFixerError(null);
                      setAddressFixerOpen(false);
                    }}
                  />
                </DialogContent>
              </Dialog>

              <CardContent className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => setListingFilter("published")}
                    className={`rounded-lg border p-4 text-left transition-colors ${
                      listingFilter === "published"
                        ? "border-primary bg-primary/5"
                        : "bg-card/40 hover:bg-card/60"
                    }`}
                  >
                    <div className="text-sm text-muted-foreground">Published Listings</div>
                    <div className="mt-1 text-2xl font-semibold">{publishedItems.length}</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setListingFilter("drafts")}
                    className={`rounded-lg border p-4 text-left transition-colors ${
                      listingFilter === "drafts"
                        ? "border-primary bg-primary/5"
                        : "bg-card/40 hover:bg-card/60"
                    }`}
                  >
                    <div className="text-sm text-muted-foreground">Draft Listings</div>
                    <div className="mt-1 text-2xl font-semibold">{draftItems.length}</div>
                  </button>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant={listingFilter === "all" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setListingFilter("all")}
                  >
                    All
                  </Button>
                  <Button
                    type="button"
                    variant={listingFilter === "published" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setListingFilter("published")}
                  >
                    Published
                  </Button>
                  <Button
                    type="button"
                    variant={listingFilter === "drafts" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setListingFilter("drafts")}
                  >
                    Drafts ({draftItems.length})
                  </Button>
                </div>

                {items.length === 0 ? (
                  <div className="rounded-lg border border-dashed p-10 text-center">
                    <Sparkles className="mx-auto mb-3 h-8 w-8 text-primary" />
                    <h3 className="font-medium">No listings yet</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Snap a photo and we'll fill in the name, description, and price for you.
                    </p>
                    <div className="mt-4 flex justify-center">
                      <Button onClick={() => setShowQuickAdd(true)}>
                        <Sparkles className="mr-2 h-4 w-4" />
                        Add a Listing
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {(listingFilter === "all" || listingFilter === "published") && publishedItems.length > 0 && (
                      <div className="space-y-3">
                        <div>
                          <h3 className="font-medium">
                            {listingFilter === "published" ? "Published Listings" : "Published"}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            Listings visible to buyers right now.
                          </p>
                        </div>

                        {publishedItems.map((item) => (
                          <div
                            key={item.id}
                            className="flex flex-col gap-4 rounded-lg border p-4 md:flex-row md:items-center md:justify-between"
                          >
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <h3 className="font-medium">{item.name}</h3>
                                <span className="rounded-full bg-muted px-2 py-0.5 text-xs capitalize">
                                  {item.category}
                                </span>
                                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700">
                                  Published
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                ZIP {item.zipCode} • Qty {item.qtyAvailable}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                ${Number(item.price).toFixed(2)}
                                {item.groceryAvg ? ` • Grocery avg $${Number(item.groceryAvg).toFixed(2)}` : ""}
                              </p>
                              {(item as any).waitingCount > 0 && (
                                <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-primary">
                                  <span aria-hidden>🔔</span>
                                  {(item as any).waitingCount}{" "}
                                  {(item as any).waitingCount === 1 ? "person is" : "people are"} waiting
                                  to buy this
                                </p>
                              )}
                              {!(item as any).unit && (
                                <p className="mt-1 flex items-center gap-1 text-xs font-medium text-amber-600">
                                  <span aria-hidden>⚠</span> No unit set — buyers can't tell what they get for the price. Tap Edit to add one.
                                </p>
                              )}
                            </div>

                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setListingCategory(toFormCategory((item as any)?.category));
                                  setListingQuickDetails(
                                    Array.isArray((item as any)?.attributesJson?.quickDetails)
                                      ? ((item as any).attributesJson.quickDetails as string[])
                                      : []
                                  );
                                  setListingStep(1);
                                  void openEditListingDialog(item);
                                }}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => setPhotoManagerItem(item)}
                              >
                                Photos
                              </Button>
                              {Number((item as any).isMeatShare ?? 0) === 1 && (
                                <Button
                                  variant="outline"
                                  onClick={() => void handleChargeShares(item.id)}
                                  disabled={chargingSharesId === item.id}
                                >
                                  Charge shares
                                </Button>
                              )}
                              <Button
                                variant="destructive"
                                onClick={() => handleDelete(item.id)}
                                disabled={deleteMutation.isPending}
                              >
                                Delete
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {(listingFilter === "all" || listingFilter === "drafts") && draftItems.length > 0 && (
                      <div className="space-y-3">
                        <div>
                          <h3 className="font-medium">
                            {listingFilter === "drafts" ? "Draft Listings" : "Drafts"}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            Not visible to buyers until published.
                          </p>
                        </div>

                        {draftItems.map((item) => {
                          const demotedReason = (item as any)?.attributesJson?.demotedReason;
                          const demotedForAddress = demotedReason === "missing_seller_address";
                          return (
                          <div
                            key={item.id}
                            className="flex flex-col gap-4 rounded-lg border border-dashed p-4 md:flex-row md:items-center md:justify-between"
                          >
                            <div className="min-w-0 space-y-2">
                              <div className="flex items-center gap-2">
                                <h3 className="font-medium">{item.name}</h3>
                                <span className="rounded-full bg-muted px-2 py-0.5 text-xs capitalize">
                                  {item.category}
                                </span>
                                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
                                  Draft
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                ZIP {item.zipCode} • Qty {item.qtyAvailable}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                ${Number(item.price).toFixed(2)}
                                {item.groceryAvg ? ` • Grocery avg $${Number(item.groceryAvg).toFixed(2)}` : ""}
                              </p>
                              {demotedForAddress && (
                                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 space-y-2">
                                  <p>
                                    This listing was unpublished because your pickup address
                                    wasn't on file. Add it and we'll let you republish.
                                  </p>
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      setAddressFixerError(null);
                                      setAddressFixerOpen(true);
                                    }}
                                  >
                                    Add your address
                                  </Button>
                                </div>
                              )}
                            </div>

                            <div className="flex gap-2">
                              {/* A draft had no way to go live from this card,
                                  and the status is never chosen anywhere —
                                  saving a complete listing is what publishes
                                  it. So a seller with a finished draft has
                                  nothing to press, and rebuilds it as a new
                                  listing instead; that is where the duplicate
                                  pairs come from. Open the dialog on its last
                                  step so the save button is right there, and
                                  let the existing readiness check either
                                  publish it or say what is missing. */}
                              <Button
                                onClick={async () => {
                                  await openEditListingDialog(item);
                                  setListingStep(5);
                                  toast({
                                    title: "Save to publish",
                                    description:
                                      "Press save and this goes live. We'll tell you if anything's still missing.",
                                  });
                                }}
                              >
                                Publish
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setListingCategory(toFormCategory((item as any)?.category));
                                  setListingQuickDetails(
                                    Array.isArray((item as any)?.attributesJson?.quickDetails)
                                      ? ((item as any).attributesJson.quickDetails as string[])
                                      : []
                                  );
                                  setListingStep(1);
                                  void openEditListingDialog(item);
                                }}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => setPhotoManagerItem(item)}
                              >
                                Photos
                              </Button>
                              {Number((item as any).isMeatShare ?? 0) === 1 && (
                                <Button
                                  variant="outline"
                                  onClick={() => void handleChargeShares(item.id)}
                                  disabled={chargingSharesId === item.id}
                                >
                                  Charge shares
                                </Button>
                              )}
                              <Button
                                variant="destructive"
                                onClick={() => handleDelete(item.id)}
                                disabled={deleteMutation.isPending}
                              >
                                Delete
                              </Button>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}

                    {listingFilter === "all" && requestItems.length > 0 && (
                      <div className="space-y-3">
                        <div>
                          <h3 className="font-medium">Open to requests</h3>
                          <p className="text-sm text-muted-foreground">
                            On the map so neighbors can ask for these. Add a photo and price to publish them for buyers.
                          </p>
                        </div>

                        {requestItems.map((item) => (
                          <div
                            key={item.id}
                            className="flex flex-col gap-4 rounded-lg border border-dashed p-4 md:flex-row md:items-center md:justify-between"
                          >
                            <div className="min-w-0 space-y-2">
                              <div className="flex items-center gap-2">
                                <h3 className="font-medium">{item.name}</h3>
                                <span className="rounded-full bg-muted px-2 py-0.5 text-xs capitalize">
                                  {item.category}
                                </span>
                                <span className="rounded-full bg-brand-secondary/15 px-2 py-0.5 text-xs text-brand-secondary">
                                  Open to requests
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                ZIP {item.zipCode} • Qty {item.qtyAvailable}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                ${Number(item.price).toFixed(2)}
                                {item.groceryAvg ? ` • Grocery avg $${Number(item.groceryAvg).toFixed(2)}` : ""}
                              </p>
                            </div>

                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setListingCategory(toFormCategory((item as any)?.category));
                                  setListingQuickDetails(
                                    Array.isArray((item as any)?.attributesJson?.quickDetails)
                                      ? ((item as any).attributesJson.quickDetails as string[])
                                      : []
                                  );
                                  setListingStep(1);
                                  void openEditListingDialog(item);
                                }}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => setPhotoManagerItem(item)}
                              >
                                Photos
                              </Button>
                              <Button
                                variant="destructive"
                                onClick={() => handleDelete(item.id)}
                                disabled={deleteMutation.isPending}
                              >
                                Delete
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {listingFilter === "published" && publishedItems.length === 0 && (
                      <div className="rounded-lg border border-dashed p-8 text-center">
                        <h3 className="font-medium">No published listings</h3>
                        <p className="mt-1 text-sm text-muted-foreground">
                          Listings with photos and ready-to-go details will appear here.
                        </p>
                      </div>
                    )}

                    {listingFilter === "drafts" && draftItems.length === 0 && (
                      <div className="rounded-lg border border-dashed p-8 text-center">
                        <h3 className="font-medium">No draft listings</h3>
                        <p className="mt-1 text-sm text-muted-foreground">
                          Drafts you save before publishing will appear here.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="orders" className="space-y-6">
            {(() => {
              const PENDING_STATUSES = new Set([
                "pending",
                "paid",
                "preparing",
                "ready_for_pickup",
                "out_for_delivery",
              ]);
              const COMPLETED_STATUSES = new Set(["picked_up", "delivered"]);
              const statusOf = (o: SellerOrder) =>
                String((o as any).status || "").toLowerCase();
              let pendingCount = 0;
              let completedCount = 0;
              let lifetimeEarnings = 0;
              for (const o of sellerOrders) {
                const status = statusOf(o);
                if (PENDING_STATUSES.has(status)) pendingCount += 1;
                if (COMPLETED_STATUSES.has(status)) {
                  completedCount += 1;
                  lifetimeEarnings += Number((o as any).sellerSubtotal ?? 0);
                }
              }
              return (
                <div className="grid gap-3 sm:grid-cols-3">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>Lifetime earnings</CardDescription>
                      <CardTitle className="text-2xl">
                        ${lifetimeEarnings.toFixed(2)}
                      </CardTitle>
                    </CardHeader>
                  </Card>
                  <button
                    type="button"
                    onClick={() =>
                      setOrderFilter((prev) => (prev === "pending" ? "all" : "pending"))
                    }
                    aria-pressed={orderFilter === "pending"}
                    data-testid="seller-orders-filter-pending"
                    className={`rounded-lg border text-left transition-colors ${
                      orderFilter === "pending"
                        ? "border-primary bg-primary/5"
                        : "bg-card hover:bg-card/80"
                    }`}
                  >
                    <CardHeader className="pb-2">
                      <CardDescription>Pending orders</CardDescription>
                      <CardTitle className="text-2xl">{pendingCount}</CardTitle>
                    </CardHeader>
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setOrderFilter((prev) => (prev === "completed" ? "all" : "completed"))
                    }
                    aria-pressed={orderFilter === "completed"}
                    data-testid="seller-orders-filter-completed"
                    className={`rounded-lg border text-left transition-colors ${
                      orderFilter === "completed"
                        ? "border-primary bg-primary/5"
                        : "bg-card hover:bg-card/80"
                    }`}
                  >
                    <CardHeader className="pb-2">
                      <CardDescription>Completed</CardDescription>
                      <CardTitle className="text-2xl">{completedCount}</CardTitle>
                    </CardHeader>
                  </button>
                </div>
              );
            })()}

            <Card>
              <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-1.5">
                  <CardTitle>Incoming Orders</CardTitle>
                  <CardDescription>
                    Review current buyer orders and their scheduled pickup or delivery details.
                  </CardDescription>
                </div>
                {isDemoAdmin && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={resetDemoOrdersMutation.isPending}
                    onClick={handleResetDemoOrders}
                  >
                    {resetDemoOrdersMutation.isPending ? "Resetting…" : "Reset demo orders"}
                  </Button>
                )}
              </CardHeader>

              <CardContent className="space-y-4">
                {(() => {
                  const PENDING_STATUSES = new Set([
                    "pending",
                    "paid",
                    "preparing",
                    "ready_for_pickup",
                    "out_for_delivery",
                  ]);
                  const COMPLETED_STATUSES = new Set(["picked_up", "delivered"]);
                  const filteredOrders = sellerOrders.filter((o) => {
                    if (orderFilter === "all") return true;
                    const status = String((o as any).status || "").toLowerCase();
                    if (orderFilter === "pending") return PENDING_STATUSES.has(status);
                    return COMPLETED_STATUSES.has(status);
                  });
                  if (sellerOrders.length === 0) {
                    return (
                      <div className="rounded-lg border border-dashed p-10 text-center">
                        <Package className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
                        <h3 className="font-medium">No orders yet</h3>
                        <p className="mt-1 text-sm text-muted-foreground">
                          Buyer orders will appear here once checkout is completed.
                        </p>
                      </div>
                    );
                  }
                  return (
                    <>
                      {orderFilter !== "all" && (
                        <div className="flex items-center justify-between rounded-md border border-dashed bg-muted/30 px-3 py-2 text-sm">
                          <span className="text-muted-foreground">
                            Showing {filteredOrders.length}{" "}
                            {orderFilter === "pending" ? "pending" : "completed"}{" "}
                            {filteredOrders.length === 1 ? "order" : "orders"}
                          </span>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => setOrderFilter("all")}
                            data-testid="seller-orders-filter-clear"
                          >
                            Show all
                          </Button>
                        </div>
                      )}
                      {filteredOrders.length === 0 ? (
                        <div className="rounded-lg border border-dashed p-10 text-center">
                          <Package className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
                          <h3 className="font-medium">
                            No {orderFilter === "pending" ? "pending" : "completed"} orders
                          </h3>
                          <p className="mt-1 text-sm text-muted-foreground">
                            {orderFilter === "pending"
                              ? "Orders in progress will appear here."
                              : "Orders you've picked up or delivered will appear here."}
                          </p>
                        </div>
                      ) : (
                        filteredOrders.map((order) => (
                          <div key={order.id} className="rounded-lg border p-4 space-y-3">
                            {order.deliveryScheduledFor && (
                              <div className="rounded-md border border-primary/30 bg-primary/5 p-2 text-xs font-medium text-primary">
                                Driver scheduled: {formatScheduledFor(order.deliveryScheduledFor)}
                              </div>
                            )}

                            {order.deliverySelfClaimable && order.deliveryDriverPay && order.deliveryRequestId && (
                              <a
                                href={`/deliveries?accept=${order.deliveryRequestId}`}
                                className="block rounded-md border border-emerald-300 bg-emerald-50 p-2 text-xs text-emerald-900 hover:bg-emerald-100"
                              >
                                <span className="font-semibold">
                                  Deliver this order yourself and earn ${Number(order.deliveryDriverPay).toFixed(2)}.
                                </span>{" "}
                                You're paid the delivery fee just like any driver — tap to claim it before another driver does.
                              </a>
                            )}

                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                              <div>
                                <h3 className="font-medium">
                                  Order #{order.id.slice(0, 8).toUpperCase()}
                                </h3>
                                <p className="text-sm text-muted-foreground">
                                  Status: <span className="font-medium text-foreground">{formatOrderStatus(order.status)}</span>
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  Total: <span className="font-medium text-foreground">${Number(order.total).toFixed(2)}</span>
                                </p>
                              </div>

                              <div className="text-sm text-muted-foreground">
                                {order.createdAt
                                  ? new Date(order.createdAt).toLocaleString()
                                  : "Recently placed"}
                              </div>
                            </div>

                            <div className="space-y-3">
                              {(() => {
                                const ADVANCE = new Set([
                                  "preparing",
                                  "ready_for_pickup",
                                  "out_for_delivery",
                                  "picked_up",
                                  "delivered",
                                ]);
                                const options = getSellerOrderStatusOptions(order);
                                const primary = options.find((o) => ADVANCE.has(o.value));
                                const rest = options.filter((o) => o !== primary);
                                return (
                                  <>
                                    {primary && (
                                      <Button
                                        type="button"
                                        size="lg"
                                        className="w-full justify-center gap-2 text-base font-semibold"
                                        onClick={() => handleSellerOrderStatusChange(order.id, primary.value)}
                                        data-testid={`seller-order-status-${order.id}-${primary.value}`}
                                      >
                                        {primary.label}
                                        <ArrowRight className="h-4 w-4" />
                                      </Button>
                                    )}
                                    <div className="flex flex-wrap gap-2">
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setLocation(`/orders/${order.id}`)}
                                        data-testid={`seller-order-view-details-${order.id}`}
                                      >
                                        View Details
                                      </Button>

                                      {!["cancel_requested", "refunded", "pending", "cancelled"].includes(
                                        String(order.status || "").trim().toLowerCase()
                                      ) && (
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant={chatOpenOrderId === order.id ? "default" : "outline"}
                                          className="relative"
                                          onClick={() =>
                                            setChatOpenOrderId((prev) =>
                                              prev === order.id ? null : order.id
                                            )
                                          }
                                          data-testid={`seller-order-chat-${order.id}`}
                                        >
                                          <MessageCircle className="mr-1.5 h-3.5 w-3.5" />
                                          Chat
                                          {(unreadByOrder[order.id] ?? 0) > 0 && (
                                            <span className="absolute -right-1 -top-1 inline-flex min-w-[1rem] h-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white">
                                              {unreadByOrder[order.id] > 9 ? "9+" : unreadByOrder[order.id]}
                                            </span>
                                          )}
                                        </Button>
                                      )}

                                      {rest.map((option) => (
                                        <Button
                                          key={option.value}
                                          type="button"
                                          size="sm"
                                          variant={option.value === "refunded" ? "ghost" : "outline"}
                                          className={
                                            option.value === "refunded"
                                              ? "text-destructive hover:text-destructive"
                                              : undefined
                                          }
                                          onClick={() => handleSellerOrderStatusChange(order.id, option.value)}
                                          data-testid={`seller-order-status-${order.id}-${option.value}`}
                                        >
                                          {option.label}
                                        </Button>
                                      ))}
                                    </div>
                                  </>
                                );
                              })()}

                              {chatOpenOrderId === order.id && (
                                <div className="mt-2">
                                  <OrderChat
                                    orderId={order.id}
                                    otherPartyName={order.buyerName || "Buyer"}
                                    recipientId={order.buyerId}
                                  />
                                </div>
                              )}

                              <div className="space-y-2">
                                {order.sellerSelections.map((selection) => (
                                  <div key={selection.id} className="rounded-md bg-muted/40 p-3 text-sm">
                                    <p className="font-medium">{selection.buyerLabel}</p>
                                    <p className="text-muted-foreground">
                                      {selection.scheduledDate} • {selection.timeWindowLabel}
                                    </p>
                                    {selection.dropZoneName ? (
                                      <p className="text-muted-foreground">
                                        {selection.dropZoneName}
                                        {selection.dropZoneCity ? ` — ${selection.dropZoneCity}` : ""}
                                        {selection.dropZoneState ? `, ${selection.dropZoneState}` : ""}
                                      </p>
                                    ) : null}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="fulfillment" className="space-y-6">
            <PickupWindowsCard />

            <Card>
              <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle>Pickup & Delivery Options</CardTitle>
                  <CardDescription>
                    Create reusable pickup and delivery choices for your items.
                  </CardDescription>
                </div>

                <Button
                  className="w-full sm:w-auto"
                  data-testid="button-add-fulfillment-profile"
                  onClick={() => {
                    resetProfileForm();
                    setReturnToListingAfterOptionSave(false);
                    setIsProfileDialogOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Option
                </Button>
              </CardHeader>

              <CardContent className="space-y-4">
                {fulfillmentProfiles.length === 0 ? (
                  <div className="rounded-lg border border-dashed p-10 text-center">
                    <Settings2 className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
                    <h3 className="font-medium">No pickup or delivery options yet</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Create a few simple pickup or delivery options for your items.
                    </p>
                  </div>
                ) : (
                  fulfillmentProfiles.map((profile) => {
                    const zone = dropZones.find((z) => z.id === profile.dropZoneId);

                    return (
                      <div key={profile.id} className="rounded-lg border p-4 space-y-2">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <h3 className="font-medium">{profile.name}</h3>
                            <p className="text-sm text-muted-foreground">
                              {METHOD_LABELS[profile.fulfillmentMethod]}
                            </p>
                          </div>
                          <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                            {Number(profile.isActive) === 1 ? "Active" : "Inactive"}
                          </span>
                        </div>

                        <p className="text-sm text-muted-foreground">
                          {(profile as any).specificDate ? (
                            <>
                              One-time date:{" "}
                              <span className="font-medium text-foreground">
                                {(profile as any).specificDate}
                              </span>
                            </>
                          ) : (
                            <>
                              Days:{" "}
                              <span className="font-medium text-foreground">
                                {getProfileDayLabel(profile)}
                              </span>
                            </>
                          )}
                        </p>

                        <p className="text-sm text-muted-foreground">
                          Time:{" "}
                          <span className="font-medium text-foreground">
                            {buildTimeSummary(profileTimeSlotsMap[profile.id] || [])}
                          </span>
                        </p>

                        {profile.description && (
                          <p className="text-sm text-muted-foreground">{profile.description}</p>
                        )}

                        {profile.fulfillmentMethod === "drop_zone_pickup" && zone && (
                          <p className="text-sm text-muted-foreground">
                            Community hub: <span className="font-medium text-foreground">{zone.name}</span>
                            {" "}({zone.city}, {zone.state})
                          </p>
                        )}

                        {profile.fulfillmentMethod === "local_delivery" && (
                          <p className="text-sm text-muted-foreground">
                            Who brings it there:{" "}
                            <span className="font-medium text-foreground">
                              {profile.movedBy === "courier"
                                ? DELIVERY_MOVED_BY_LABELS.courier
                                : DELIVERY_MOVED_BY_LABELS.seller}
                            </span>
                          </p>
                        )}

                        <div className="flex gap-2 pt-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => openEditProfileDialog(profile)}
                          >
                            Edit
                          </Button>

                          {Number(profile.isActive) === 1 && (
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => applyProfileToAllMutation.mutate(profile.id)}
                              disabled={applyProfileToAllMutation.isPending}
                            >
                              Apply to all listings
                            </Button>
                          )}

                          {Number(profile.isActive) === 1 && (
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => handleDeleteProfile(profile)}
                              disabled={deleteProfileMutation.isPending}
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <ListingPhotoManager
        item={photoManagerItem}
        maxPhotos={(user as any)?.storefrontGalleryEnabled === 1 ? 20 : 8}
        onOpenChange={(open) => {
          if (!open) setPhotoManagerItem(null);
        }}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ["/api/seller/items"] });
        }}
      />
    </div>
  );
}
